import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { getRoleLabel, hasSuperAdminRole, ROLE_OPTIONS } from '../utils/roles';
import { analyticsService, AnalyticsSectionData } from '../services/analyticsService';
import { supabase } from '../services/supabase';
import { performanceMonitorService, PerformanceSnapshot } from '../services/performanceMonitorService';
import { appErrorMonitorService, AppErrorEvent, AppErrorLevel } from '../services/appErrorMonitorService';
import {
  telemetryMonitorService,
  SessionLossEnforcementSummary,
  TelemetryEventRecord,
  TelemetryLevel,
  MonitoringAlertRecord,
  MonitoringAlertRunRecord,
} from '../services/telemetryMonitorService';
import {
  appConfigService,
  AppAsset,
  AppSettings,
  FeatureFlags,
  HeaderImagesConfig,
  NotificationSettings,
  PushNotificationRoleConfig,
  QuickActionsConfig,
  ThemeColors,
} from '../services/appConfigService';
import {
  QUICK_ACTION_ALIASES,
  QUICK_ACTION_DEFINITIONS,
  getQuickActionAdminPreviewUrl,
} from '../shared/quickActions';
import { SuperAdminMonitoringTab } from '../components/SuperAdminMonitoringTab';
import { SuperAdminPerformanceSolutionsTab } from '../components/SuperAdminPerformanceSolutionsTab';
import { SuperAdminCrashesTab } from '../components/SuperAdminCrashesTab';
import { SuperAdminSecurityTab } from '../components/SuperAdminSecurityTab';

export {};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  padding: 1.5rem;
`;


const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--accent);
    background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surfaceVariant)')};
  }
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const CardSubtitle = styled.p`
  margin: 0 0 1rem;
  color: var(--onSurfaceVariant);
`;

const LogTableWrap = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const LogTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.7rem 0.9rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.85rem;
    color: var(--onSurfaceVariant);
    vertical-align: top;
  }

  th {
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.7rem;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.65rem;
      font-size: 0.8rem;
    }
  }
`;

const ChangeList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
`;

const ChangeItem = styled.li`
  margin: 0.15rem 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const AnalyticsSectionsGrid = styled.div`
  display: grid;
  gap: 1.1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const PerformanceGrid = styled(Grid)`
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PerformanceTabStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const PerfSubCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  border: 1px solid var(--outline);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface)) 0%,
    var(--surface) 48%
  );
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
`;

const PerfSubCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PerfSubCardTitle = styled.h3`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--onSurface);
`;

const PerfSubCardDesc = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--onSurfaceVariant);
`;

const PerfToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem 0.75rem;
`;

const PerfToolbarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
`;

const PerfToolbarSpacer = styled.div`
  flex: 1;
  min-width: 0.5rem;
`;

const PerfToolbarMeta = styled.span`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  white-space: nowrap;
`;

const PerfGhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const PerfStatTile = styled.div`
  border-radius: 14px;
  border: 1px solid var(--outline);
  background: var(--surface);
  padding: 0.9rem 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const PerfTableShell = styled.div`
  border-radius: 14px;
  border: 1px solid var(--outline);
  background: var(--surface);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const PerfTableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-height: min(58vh, 520px);
`;

const PerfTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.81rem;

  th,
  td {
    padding: 0.65rem 0.85rem;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid var(--outline);
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: 0 1px 0 var(--outline);
  }

  tbody tr {
    transition: background 0.12s ease;
  }

  tbody tr:nth-child(even) {
    background: color-mix(in srgb, var(--surfaceVariant) 35%, var(--surface));
  }

  tbody tr:hover td {
    background: color-mix(in srgb, var(--primary) 10%, var(--surface));
  }

  tbody td {
    color: var(--onSurface);
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.65rem;
      font-size: 0.78rem;
    }
  }
`;

const PerfPaginationBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  padding: 0.65rem 0.85rem;
  background: var(--surfaceVariant);
  border-top: 1px solid var(--outline);
`;

const PerfPageMeta = styled.span`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  line-height: 1.4;
  min-width: 0;
`;

const PerfPageNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
`;

const PerfPageIndicator = styled.span`
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
  padding: 0 0.35rem;

  strong {
    color: var(--onSurface);
    font-weight: 700;
  }
`;

const PerfNavButton = styled.button`
  height: 34px;
  padding: 0 0.55rem;
  white-space: nowrap;
  border-radius: 9px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;

  &:hover:not(:disabled) {
    border-color: var(--primary);
    background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  }

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }
`;

const QuickActionsGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
`;

const QuickActionTile = styled.div<{ $dimmed: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  opacity: ${({ $dimmed }) => ($dimmed ? 0.52 : 1)};
  transition: opacity 0.15s ease;
`;

const QuickActionTileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const QuickActionTileBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.45rem 0.55rem;
  background: rgba(0, 0, 0, 0.68);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const QuickActionTileTitle = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
  flex: 1;
  min-width: 0;
`;

const QuickActionTileToggleWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #fff;
  font-size: 0.72rem;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
`;

const MetricLabel = styled.p`
  margin: 0 0 0.4rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  display: flex;
  align-items: center;
`;

const MetricValue = styled.div<{ $bad?: boolean }>`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ $bad }) => ($bad ? 'var(--error)' : 'var(--onSurface)')};
`;

const ChartWrap = styled.div`
  margin-top: 0.6rem;
  height: 44px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(220px, 2fr);
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--onSurface);
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 0.9rem;
  min-height: 90px;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const Toggle = styled.input`
  width: 18px;
  height: 18px;
`;

const SaveButton = styled.button`
  background: var(--primary);
  border: none;
  color: var(--onPrimary);
  border-radius: 12px;
  padding: 0.6rem 1.1rem;
  cursor: pointer;
  font-weight: 600;
  align-self: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ $type: 'success' | 'error' }>`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: ${({ $type }) => ($type === 'success' ? 'var(--successContainer)' : 'var(--errorContainer)')};
  color: ${({ $type }) => ($type === 'success' ? 'var(--onSuccessContainer)' : 'var(--onErrorContainer)')};
  border: 1px solid ${({ $type }) => ($type === 'success' ? 'var(--success)' : 'var(--error)')};
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--outline);
  margin: 0.75rem 0;
`;

const AssetRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--outline);
`;

const SmallText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const AnalyticsMetricsGrid = styled.div`
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fill, minmax(176px, 1fr));
`;

const AnalyticsMetricTile = styled.div<{ $full?: boolean }>`
  grid-column: ${({ $full }) => ($full ? '1 / -1' : 'auto')};
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surface);
  padding: 0.7rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

const AnalyticsMetricName = styled.span`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: var(--onSurfaceVariant);
`;

const AnalyticsMetricBody = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--onSurface);
  line-height: 1.45;
  word-break: break-word;

  p {
    margin: 0;
    font-weight: 400;
    font-size: 0.84rem;
    color: var(--onSurfaceVariant);
  }

  p + p {
    margin-top: 0.4rem;
  }
`;

const RoleOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.35rem 0.75rem;
`;

const RoleOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #4a5568;
`;


const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid var(--outline);
  color: var(--onSurfaceVariant);
  font-size: 0.7rem;
  margin-left: 6px;
  cursor: help;
`;

const ExternalLink = styled.a`
  color: var(--accent);
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const StoreAdminActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const StoreAdminLink = styled.a<{ $secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid ${({ $secondary }) => ($secondary ? 'var(--outline)' : 'var(--primary)')};
  background: ${({ $secondary }) => ($secondary ? 'var(--surface)' : 'var(--primary)')};
  color: ${({ $secondary }) => ($secondary ? 'var(--onSurface)' : 'var(--onPrimary)')};
`;

const EmptyState = styled.p`
  color: var(--onSurfaceVariant);
  font-style: italic;
  margin: 0;
`;

const PerfFeedback = styled.div<{ $tone: 'error' | 'warning' | 'info' }>`
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--outline)'};
  background: ${({ $tone }) =>
    $tone === 'error'
      ? 'color-mix(in srgb, var(--error) 14%, var(--surface))'
      : $tone === 'warning'
        ? 'color-mix(in srgb, var(--warning) 14%, var(--surface))'
        : 'color-mix(in srgb, var(--info) 12%, var(--surface))'};
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--onWarningContainer)' : 'var(--onSurface)'};
  font-size: 0.84rem;
  line-height: 1.45;
`;

const DangerButton = styled.button`
  background: var(--error);
  color: var(--onError);
  border: none;
  border-radius: 10px;
  padding: 0.45rem 0.85rem;
  cursor: pointer;
`;

const PerfPrimaryButton = styled(SaveButton)`
  border-radius: 10px;
  padding: 0.48rem 1rem;
  font-size: 0.82rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
`;

const PerfDangerButton = styled(DangerButton)`
  border-radius: 10px;
  padding: 0.48rem 0.95rem;
  font-size: 0.82rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PerfSelect = styled(Select)`
  width: auto;
  min-width: 128px;
  max-width: 200px;
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
  border-radius: 10px;
`;

const PerfSearchInput = styled(TextInput)`
  max-width: min(100%, 280px);
`;

const UploadButton = styled.button`
  background: var(--surfaceVariant);
  color: var(--onSurface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled(Card)`
  width: min(720px, 92vw);
  max-height: 80vh;
  overflow: auto;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--error);
  cursor: pointer;
  padding: 0;
  font: inherit;
  text-decoration: underline;
`;

const DetailButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  padding: 0;
  font: inherit;
  text-decoration: underline;
`;

const LevelBadge = styled.span<{ $level: AppErrorLevel }>`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $level }) =>
    $level === 'error' ? 'var(--errorContainer)' : $level === 'warning' ? 'var(--warningContainer)' : 'var(--surfaceVariant)'};
  color: ${({ $level }) =>
    $level === 'error' ? 'var(--error)' : $level === 'warning' ? 'var(--onWarningContainer)' : 'var(--info)'};
`;

const CodeBlock = styled.pre`
  margin: 0;
  padding: 0.75rem;
  background: var(--surfaceVariant);
  border-radius: 8px;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
  white-space: pre-wrap;
  word-break: break-word;
`;

const toNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeVersionInput = (
  value: unknown,
  fallback: string,
) => {
  const normalized = String(value ?? '').trim();
  if (!normalized || normalized === '0') return fallback;
  return normalized;
};

const normalizeAppSettingsForForm = (
  settings: AppSettingsWithHomeLogo,
): AppSettingsWithHomeLogo => {
  const fallbackVersion = String(settings.app_version ?? '').trim() || '1.0.0';

  return {
    ...settings,
    latest_ios_build: normalizeVersionInput(settings.latest_ios_build, fallbackVersion),
    latest_android_build: normalizeVersionInput(settings.latest_android_build, fallbackVersion),
  };
};

const listToString = (values?: string[]) => (values && values.length ? values.join(', ') : '');
const stringToList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const formatMetricLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatMetricValue = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    if (!value.length) return '';
    const hasObjects = value.some((item) => item && typeof item === 'object');
    return hasObjects ? JSON.stringify(value) : value.join(', ');
  }
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const compactId = (value?: string | null) => {
  if (!value) return '';
  return value.length > 10 ? `${value.slice(0, 8)}` : value;
};

const truncateText = (value?: string | null, max = 120) => {
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max)}`;
};

const formatJsonBlock = (value: unknown) => {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const renderMetricValue = (value: unknown) => {
  if (Array.isArray(value) && value.some((item) => item && typeof item === 'object')) {
    const items = value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
    if (!items.length) return <SmallText></SmallText>;
    return (
      <Section>
        {items.map((item, index) => {
          const title =
            item.title ??
            item.name ??
            item.label ??
            item.id ??
            `Item ${index + 1}`;
          const likes = item.likes ?? item.like_count ?? item.likeCount;
          const comments = item.comments ?? item.comment_count ?? item.commentCount;
          const count = item.count ?? item.total ?? item.value;
          const completions = item.completions ?? item.completed ?? item.completed_count ?? item.completion_count;
          const devotionals = item.devotionals ?? item.devotional_count ?? item.devotional_total;
          const users = item.unique_users ?? item.user_count ?? item.users;
          const parts: string[] = [];
          if (likes !== undefined) parts.push(`Likes: ${formatMetricValue(likes)}`);
          if (comments !== undefined) parts.push(`Comments: ${formatMetricValue(comments)}`);
          if (count !== undefined) parts.push(`Count: ${formatMetricValue(count)}`);
          if (completions !== undefined) parts.push(`Completions: ${formatMetricValue(completions)}`);
          if (devotionals !== undefined) parts.push(`Devotionals: ${formatMetricValue(devotionals)}`);
          if (users !== undefined) parts.push(`Users: ${formatMetricValue(users)}`);
          const suffix = parts.length ? `  ${parts.join('  ')}` : '';
          return (
            <SmallText key={`${String(title)}-${index}`}>
              {String(title)}
              {suffix}
            </SmallText>
          );
        })}
      </Section>
    );
  }

  return <SmallText>{formatMetricValue(value)}</SmallText>;
};

const renderMetricHeader = (label: string, description: string) => (
  <span>
    {label}
    <InfoIcon title={description}>?</InfoIcon>
  </span>
);

const toNumberSafe = (value?: number | null) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const renderSparkline = (values: number[], color = 'var(--primary)') => {
  if (!values.length) return <SmallText></SmallText>;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values
    .map((value, index) => {
      const x = (index / Math.max(1, values.length - 1)) * 100;
      const y = 40 - ((value - min) / range) * 40;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 100 40" width="100%" height="40" aria-hidden>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

const inferAssetType = (mimeType?: string): AppAsset['type'] => {
  if (!mimeType) return 'document';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
};

const SUPER_ADMIN_TABS = [
  { id: 'theme', label: 'Theme' },
  { id: 'quick-actions', label: 'Quick Actions' },
  { id: 'headers', label: 'Headers' },
  { id: 'settings', label: 'Settings' },
  { id: 'store', label: 'Store' },
  { id: 'features', label: 'Features' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'assets', label: 'Assets' },
  { id: 'edit-log', label: 'Edit Log' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'security', label: 'Security' },
  { id: 'performance', label: 'Performance' },
] as const;

type SuperAdminTab = (typeof SUPER_ADMIN_TABS)[number]['id'];
type AppSettingsWithHomeLogo = AppSettings & { home_logo_url?: string };
type EditLogUserRef = { id: string; full_name?: string | null; email?: string | null };
type UserEditLog = {
  id: string;
  created_at: string;
  source_table: string;
  action: string;
  entity_id?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
  actor_roles?: string[] | null;
  changes: Record<string, { old: unknown; new: unknown }>;
  user?: EditLogUserRef | null;
  actor?: EditLogUserRef | null;
};

type AppErrorLevelFilter = 'all' | AppErrorLevel;
type TelemetryLevelFilter = 'all' | TelemetryLevel;
type AlertSeverityFilter = 'all' | MonitoringAlertRecord['severity'];
type AlertStatusFilter = 'all' | MonitoringAlertRecord['status'];
type PerformanceSubTab = 'logs' | 'crashes' | 'fixes';

type PushNotificationRoleRow = {
  key: string;
  label: string;
};

type PerfFeedbackState = {
  tone: 'error' | 'warning' | 'info';
  text: string;
};

const PUSH_NOTIFICATION_ROLE_ROWS: PushNotificationRoleRow[] = [
  { key: 'kids_church_check_in', label: 'Kids Church Check-in' },
  { key: 'kids_church_check_out', label: 'Kids Church Check-out' },
  { key: 'verification_request', label: 'New Verification Request' },
  { key: 'report_submitted', label: 'New Report Submitted' },
  { key: 'event_submission', label: 'Event Submission' },
];

const DEFAULT_PUSH_NOTIFICATION_ROLE_CONFIG: PushNotificationRoleConfig = {
  kids_church_check_in: ['teacher'],
  kids_church_check_out: ['teacher'],
  verification_request: ['admin', 'super_admin'],
  report_submitted: ['admin', 'super_admin'],
  event_submission: ['admin', 'super_admin', 'editor'],
};

const formatChangeValue = (value: unknown) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim() ? value : '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};

const formatSourceLabel = (source?: string | null, action?: string | null) => {
  const sourceLabel = source ? source.replace(/_/g, ' ') : 'unknown';
  const actionLabel = action ? action.toLowerCase() : 'update';
  return `${sourceLabel}  ${actionLabel}`;
};

const normalizeLookupLabel = (label?: string | null) => {
  if (!label) return null;
  return label.trim() || null;
};

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const shouldHideChangeField = (field: string) =>
  field === 'id' || field === 'created_at' || field === 'author_id' || field === 'member_id';

const normalizeQuickActionsConfig = (value: QuickActionsConfig | null): QuickActionsConfig | null => {
  if (!value || typeof value !== 'object') return null;

  const normalized: QuickActionsConfig = {};

  Object.entries(value).forEach(([rawKey, action]) => {
    const key = QUICK_ACTION_ALIASES[rawKey] ?? rawKey;
    const definition = QUICK_ACTION_DEFINITIONS.find((item) => item.id === key);
    const previous = normalized[key];

    normalized[key] = {
      title: action?.title ?? previous?.title ?? definition?.title ?? key,
      icon: action?.icon ?? previous?.icon ?? definition?.icon ?? '',
      color: action?.color ?? previous?.color ?? '',
      action: action?.action ?? previous?.action ?? key,
      backgroundImage: action?.backgroundImage ?? previous?.backgroundImage,
      enabled: action?.enabled === false ? false : true,
    };
  });

  QUICK_ACTION_DEFINITIONS.forEach((definition) => {
    if (normalized[definition.id]) return;
    normalized[definition.id] = {
      title: definition.title,
      icon: definition.icon,
      color: '',
      action: definition.id,
      enabled: true,
    };
  });

  return normalized;
};

type PerformanceTablePagerProps = {
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number | null;
  rowCount: number;
  onPageChange: (nextPage: number) => void;
};

const PerformanceTablePager: React.FC<PerformanceTablePagerProps> = ({
  loading,
  page,
  pageSize,
  totalCount,
  rowCount,
  onPageChange,
}) => {
  const totalPages =
    totalCount !== null ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;
  const lastPageIndex = totalPages !== null ? totalPages - 1 : null;

  const canGoPrev = !loading && page > 0;
  const canGoNext =
    !loading && (totalPages !== null ? page < totalPages - 1 : rowCount >= pageSize);
  const canGoFirst = canGoPrev;
  const canGoLast = !loading && lastPageIndex !== null && page < lastPageIndex;

  const rangeText = (() => {
    if (loading && rowCount === 0) return 'Loading';
    if (rowCount === 0) {
      if (totalCount !== null && totalCount > 0) return 'No rows on this page';
      return 'No records';
    }
    if (totalCount !== null && totalCount > 0) {
      const from = page * pageSize + 1;
      const to = Math.min(page * pageSize + rowCount, totalCount);
      return `Showing ${from.toLocaleString()}S${to.toLocaleString()} of ${totalCount.toLocaleString()}`;
    }
    return `${rowCount} row${rowCount === 1 ? '' : 's'} on this page`;
  })();

  const pageIndicator =
    totalPages !== null ? (
      <PerfPageIndicator>
        Page <strong>{page + 1}</strong> / {totalPages}
      </PerfPageIndicator>
    ) : (
      <PerfPageIndicator>
        Page <strong>{page + 1}</strong>
      </PerfPageIndicator>
    );

  return (
    <PerfPaginationBar>
      <PerfPageMeta>{rangeText}</PerfPageMeta>
      <PerfPageNav role="navigation" aria-label="Table pagination">
        <PerfNavButton
          type="button"
          disabled={!canGoFirst}
          onClick={() => onPageChange(0)}
          title="First page"
          aria-label="First page"
        >
           First
        </PerfNavButton>
        <PerfNavButton
          type="button"
          disabled={!canGoPrev}
          onClick={() => onPageChange(Math.max(0, page - 1))}
          title="Previous page"
          aria-label="Previous page"
        >
           Prev
        </PerfNavButton>
        {pageIndicator}
        <PerfNavButton
          type="button"
          disabled={!canGoNext}
          onClick={() => onPageChange(page + 1)}
          title="Next page"
          aria-label="Next page"
        >
          Next 
        </PerfNavButton>
        <PerfNavButton
          type="button"
          disabled={!canGoLast}
          onClick={() => onPageChange(lastPageIndex ?? page)}
          title="Last page"
          aria-label="Last page"
        >
          Last 
        </PerfNavButton>
      </PerfPageNav>
    </PerfPaginationBar>
  );
};

export const SuperAdminPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile, isLoading } = useCurrentUserProfile(user?.id);
  const isSuperAdmin = hasSuperAdminRole(profile?.roles ?? user?.user_metadata?.roles);

  const [activeTab, setActiveTab] = useState<SuperAdminTab>('theme');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [themeColors, setThemeColors] = useState<ThemeColors | null>(null);
  const [quickActions, setQuickActions] = useState<QuickActionsConfig | null>(null);
  const [headerImages, setHeaderImages] = useState<HeaderImagesConfig | null>(null);
  const [appSettings, setAppSettings] = useState<AppSettingsWithHomeLogo | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const [pushNotificationRoleConfig, setPushNotificationRoleConfig] = useState<PushNotificationRoleConfig | null>(null);
  const [assets, setAssets] = useState<AppAsset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingHomeLogo, setUploadingHomeLogo] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const homeLogoInputRef = useRef<HTMLInputElement>(null);
  const [logoPrefilled, setLogoPrefilled] = useState(false);
  const [quickActionImageFailed, setQuickActionImageFailed] = useState<Record<string, boolean>>({});
  const [analyticsSections, setAnalyticsSections] = useState<AnalyticsSectionData[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [performanceSnapshots, setPerformanceSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  const [selectedRealtimeSnapshot, setSelectedRealtimeSnapshot] = useState<PerformanceSnapshot | null>(null);
  const [correlatedTelemetryEvents, setCorrelatedTelemetryEvents] = useState<TelemetryEventRecord[]>([]);
  const [correlatedAppErrorEvents, setCorrelatedAppErrorEvents] = useState<AppErrorEvent[]>([]);
  const [correlatedEventsLoading, setCorrelatedEventsLoading] = useState(false);
  const [performancePage, setPerformancePage] = useState(0);
  const [performancePageSize, setPerformancePageSize] = useState(50);
  const [performanceTotalCount, setPerformanceTotalCount] = useState<number | null>(null);
  const [performanceUserLookup, setPerformanceUserLookup] = useState<Record<string, string>>({});
  const [performanceFeedback, setPerformanceFeedback] = useState<PerfFeedbackState | null>(null);
  const [performanceSubTab, setPerformanceSubTab] = useState<PerformanceSubTab>('logs');
  const [appErrorEvents, setAppErrorEvents] = useState<AppErrorEvent[]>([]);
  const [appErrorLoading, setAppErrorLoading] = useState(false);
  const [appErrorError, setAppErrorError] = useState<string | null>(null);
  const [appErrorPage, setAppErrorPage] = useState(0);
  const [appErrorPageSize, setAppErrorPageSize] = useState(25);
  const [appErrorTotalCount, setAppErrorTotalCount] = useState<number | null>(null);
  const [appErrorLevelFilter, setAppErrorLevelFilter] = useState<AppErrorLevelFilter>('all');
  const [appErrorSearch, setAppErrorSearch] = useState('');
  const [appErrorSearchInput, setAppErrorSearchInput] = useState('');
  const [selectedAppErrorEvent, setSelectedAppErrorEvent] = useState<AppErrorEvent | null>(null);
  const [appErrorUserLookup, setAppErrorUserLookup] = useState<Record<string, string>>({});
  const [appErrorFeedback, setAppErrorFeedback] = useState<PerfFeedbackState | null>(null);
  const [telemetryEvents, setTelemetryEvents] = useState<TelemetryEventRecord[]>([]);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [telemetryError, setTelemetryError] = useState<string | null>(null);
  const [telemetryPage, setTelemetryPage] = useState(0);
  const [telemetryPageSize, setTelemetryPageSize] = useState(25);
  const [telemetryTotalCount, setTelemetryTotalCount] = useState<number | null>(null);
  const [telemetryLevelFilter, setTelemetryLevelFilter] = useState<TelemetryLevelFilter>('all');
  const [telemetrySourceFilter, setTelemetrySourceFilter] = useState('all');
  const [telemetryEventFilter, setTelemetryEventFilter] = useState('all');
  const [telemetrySearch, setTelemetrySearch] = useState('');
  const [telemetrySearchInput, setTelemetrySearchInput] = useState('');
  const [selectedTelemetryEvent, setSelectedTelemetryEvent] = useState<TelemetryEventRecord | null>(null);
  const [telemetryFeedback, setTelemetryFeedback] = useState<PerfFeedbackState | null>(null);
  const [telemetryUserLookup, setTelemetryUserLookup] = useState<Record<string, string>>({});
  const [sessionLossSummary, setSessionLossSummary] = useState<SessionLossEnforcementSummary[]>([]);
  const [sessionLossSummaryLoading, setSessionLossSummaryLoading] = useState(false);
  const [sessionLossSummaryError, setSessionLossSummaryError] = useState<string | null>(null);
  const [monitoringAlerts, setMonitoringAlerts] = useState<MonitoringAlertRecord[]>([]);
  const [monitoringAlertsLoading, setMonitoringAlertsLoading] = useState(false);
  const [monitoringAlertsError, setMonitoringAlertsError] = useState<string | null>(null);
  const [monitoringAlertsPage, setMonitoringAlertsPage] = useState(0);
  const [monitoringAlertsPageSize, setMonitoringAlertsPageSize] = useState(25);
  const [monitoringAlertsTotalCount, setMonitoringAlertsTotalCount] = useState<number | null>(null);
  const [monitoringAlertSeverityFilter, setMonitoringAlertSeverityFilter] = useState<AlertSeverityFilter>('all');
  const [monitoringAlertStatusFilter, setMonitoringAlertStatusFilter] = useState<AlertStatusFilter>('all');
  const [monitoringAlertRuns, setMonitoringAlertRuns] = useState<MonitoringAlertRunRecord[]>([]);
  const [monitoringAlertsFeedback, setMonitoringAlertsFeedback] = useState<PerfFeedbackState | null>(null);
  const [editLogs, setEditLogs] = useState<UserEditLog[]>([]);
  const [editLogsLoading, setEditLogsLoading] = useState(false);
  const [editLogsError, setEditLogsError] = useState<string | null>(null);
  const [editLogSourceFilter, setEditLogSourceFilter] = useState<string>('all');
  const [editLogSearch, setEditLogSearch] = useState('');
  const [editLogFromDate, setEditLogFromDate] = useState('');
  const [editLogToDate, setEditLogToDate] = useState('');
  const [groupEditEvents, setGroupEditEvents] = useState(true);
  const [householdLookup, setHouseholdLookup] = useState<Record<string, string>>({});
  const [volunteerCategoryLookup, setVolunteerCategoryLookup] = useState<Record<string, string>>({});
  const [pushNotificationRolesSaving, setPushNotificationRolesSaving] = useState(false);
  const [pushNotificationRolesDirty, setPushNotificationRolesDirty] = useState(false);

  const showMessage = useCallback((type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const loadAllConfigurations = useCallback(async () => {
    setLoading(true);
    try {
      const [
        themeResult,
        quickActionsResult,
        headerImagesResult,
        appSettingsResult,
        featureFlagsResult,
        notificationSettingsResult,
        pushNotificationRolesResult,
        assetsResult,
      ] = await Promise.all([
        appConfigService.getThemeColors(),
        appConfigService.getQuickActions(),
        appConfigService.getHeaderImages(),
        appConfigService.getAppSettings(),
        appConfigService.getFeatureFlags(),
        appConfigService.getNotificationSettings(),
        appConfigService.getPushNotificationRoleConfig(),
        appConfigService.getAllAssets(),
      ]);

      if (themeResult.success && themeResult.data) setThemeColors(themeResult.data);
      if (quickActionsResult.success) {
        setQuickActions(normalizeQuickActionsConfig((quickActionsResult.data ?? {}) as QuickActionsConfig));
      }
      if (headerImagesResult.success && headerImagesResult.data) setHeaderImages(headerImagesResult.data);
      if (appSettingsResult.success && appSettingsResult.data) {
        setAppSettings(normalizeAppSettingsForForm(appSettingsResult.data));
      }
      if (featureFlagsResult.success && featureFlagsResult.data) setFeatureFlags(featureFlagsResult.data);
      if (notificationSettingsResult.success && notificationSettingsResult.data)
        setNotificationSettings(notificationSettingsResult.data);
      if (pushNotificationRolesResult.success) {
        const mergedConfig = {
          ...DEFAULT_PUSH_NOTIFICATION_ROLE_CONFIG,
          ...(pushNotificationRolesResult.data ?? {}),
        };
        setPushNotificationRoleConfig(mergedConfig);
      }
      if (assetsResult.success && assetsResult.data) setAssets(assetsResult.data);
    } catch (error) {
      showMessage('error', 'Error loading configurations.');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const result = await analyticsService.getDashboardSections();
      if (result.data) {
        setAnalyticsSections(result.data);
      }
      if (!result.success) {
        setAnalyticsError(result.message || 'Some analytics sections failed to load.');
      }
    } catch (error) {
      setAnalyticsError('Failed to load analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadPerformanceSnapshots = useCallback(async () => {
    setPerformanceLoading(true);
    setPerformanceError(null);
    setPerformanceFeedback({ tone: 'info', text: 'Refreshing performance snapshots...' });
    try {
      const offset = performancePage * performancePageSize;
      const result = await performanceMonitorService.getSnapshotsPage(performancePageSize, offset);
      if (result.success && result.data) {
        setPerformanceSnapshots(result.data);
        setPerformanceTotalCount(result.count ?? null);
        if (!result.data.length) {
          setPerformanceFeedback({
            tone: 'info',
            text: 'No performance snapshots available for this page yet.',
          });
        } else {
          setPerformanceFeedback({
            tone: 'info',
            text: `Loaded ${result.data.length} performance snapshot${result.data.length === 1 ? '' : 's'} for this page.`,
          });
        }
        const ids = Array.from(
          new Set(
            result.data
              .map((snapshot) => snapshot.user_id)
              .filter((userId): userId is string => Boolean(userId))
          )
        );
        if (!ids.length) {
          setPerformanceUserLookup({});
        } else {
          const { data, error } = await supabase
            .from('users')
            .select('id, full_name, email')
            .in('id', ids);

          if (!error) {
            const next: Record<string, string> = {};
            (data ?? []).forEach((row: { id: string; full_name?: string | null; email?: string | null }) => {
              const label = normalizeLookupLabel(row.full_name) ?? normalizeLookupLabel(row.email) ?? row.id;
              next[row.id] = label;
            });
            setPerformanceUserLookup((prev) => ({ ...prev, ...next }));
          }
        }
      } else {
        setPerformanceError(result.message || 'Failed to load performance snapshots.');
        setPerformanceFeedback({ tone: 'error', text: result.message || 'Failed to load performance snapshots.' });
      }
    } catch (error) {
      setPerformanceError('Failed to load performance snapshots.');
      setPerformanceFeedback({ tone: 'error', text: 'Failed to load performance snapshots.' });
    } finally {
      setPerformanceLoading(false);
    }
  }, [performancePage, performancePageSize]);

  const handleClearPerformanceSnapshots = useCallback(async () => {
    const confirmed = window.confirm('Clear all performance snapshots? This action cannot be undone.');
    if (!confirmed) return;
    setPerformanceLoading(true);
    setPerformanceError(null);
    setPerformanceFeedback({ tone: 'info', text: 'Clearing performance snapshots...' });
    try {
      const result = await performanceMonitorService.clearSnapshots();
      if (result.success) {
        setPerformancePage(0);
        await loadPerformanceSnapshots();
        setPerformanceFeedback({ tone: 'warning', text: 'All performance snapshots were cleared.' });
      } else {
        setPerformanceError(result.message || 'Failed to clear performance snapshots.');
        setPerformanceFeedback({ tone: 'error', text: result.message || 'Failed to clear performance snapshots.' });
      }
    } catch {
      setPerformanceError('Failed to clear performance snapshots.');
      setPerformanceFeedback({ tone: 'error', text: 'Failed to clear performance snapshots.' });
    } finally {
      setPerformanceLoading(false);
    }
  }, [loadPerformanceSnapshots]);

  const loadAppErrorUserLookup = useCallback(async (events: AppErrorEvent[]) => {
    const ids = Array.from(
      new Set(
        events
          .map((event) => event.user_id)
          .filter((userId): userId is string => Boolean(userId))
      )
    );
    if (!ids.length) {
      setAppErrorUserLookup({});
      return;
    }

    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, email')
      .in('id', ids);

    if (error) {
      return;
    }

    const next: Record<string, string> = {};
    (data ?? []).forEach((row: { id: string; full_name?: string | null; email?: string | null }) => {
      const label = normalizeLookupLabel(row.full_name) ?? normalizeLookupLabel(row.email) ?? row.id;
      next[row.id] = label;
    });
    setAppErrorUserLookup((prev) => ({ ...prev, ...next }));
  }, []);

  const loadAppErrorEvents = useCallback(async () => {
    setAppErrorLoading(true);
    setAppErrorError(null);
    setAppErrorFeedback({ tone: 'info', text: 'Refreshing app error events...' });
    try {
      const offset = appErrorPage * appErrorPageSize;
      const level = appErrorLevelFilter === 'all' ? undefined : appErrorLevelFilter;
      const search = appErrorSearch.trim() || undefined;
      const result = await appErrorMonitorService.getEventsPage(appErrorPageSize, offset, { level, search });
      if (result.success && result.data) {
        setAppErrorEvents(result.data);
        setAppErrorTotalCount(result.count ?? null);
        if (!result.data.length) {
          setAppErrorFeedback({ tone: 'info', text: 'No app error events match the current filters.' });
        } else {
          setAppErrorFeedback({
            tone: 'info',
            text: `Loaded ${result.data.length} app error event${result.data.length === 1 ? '' : 's'} for this page.`,
          });
        }
        await loadAppErrorUserLookup(result.data);
      } else {
        setAppErrorError(result.message || 'Failed to load app error events.');
        setAppErrorFeedback({ tone: 'error', text: result.message || 'Failed to load app error events.' });
      }
    } catch (error) {
      setAppErrorError('Failed to load app error events.');
      setAppErrorFeedback({ tone: 'error', text: 'Failed to load app error events.' });
    } finally {
      setAppErrorLoading(false);
    }
  }, [appErrorLevelFilter, appErrorPage, appErrorPageSize, appErrorSearch, loadAppErrorUserLookup]);

  const handleClearAppErrorEvents = useCallback(async () => {
    const confirmed = window.confirm('Clear all app error events? This action cannot be undone.');
    if (!confirmed) return;
    setAppErrorLoading(true);
    setAppErrorError(null);
    setAppErrorFeedback({ tone: 'info', text: 'Clearing app error events...' });
    try {
      const result = await appErrorMonitorService.clearEvents();
      if (result.success) {
        setAppErrorPage(0);
        await loadAppErrorEvents();
        setAppErrorFeedback({ tone: 'warning', text: 'All app error events were cleared.' });
      } else {
        setAppErrorError(result.message || 'Failed to clear app error events.');
        setAppErrorFeedback({ tone: 'error', text: result.message || 'Failed to clear app error events.' });
      }
    } catch {
      setAppErrorError('Failed to clear app error events.');
      setAppErrorFeedback({ tone: 'error', text: 'Failed to clear app error events.' });
    } finally {
      setAppErrorLoading(false);
    }
  }, [loadAppErrorEvents]);

  const loadTelemetryUserLookup = useCallback(async (events: TelemetryEventRecord[]) => {
    const ids = Array.from(
      new Set(
        events
          .map((event) => event.user_id)
          .filter((userId): userId is string => Boolean(userId))
      )
    );
    if (!ids.length) {
      setTelemetryUserLookup({});
      return;
    }
    const { data, error } = await supabase
      .from('users')
      .select('id, full_name, email')
      .in('id', ids);
    if (error) return;
    const next: Record<string, string> = {};
    (data ?? []).forEach((row: { id: string; full_name?: string | null; email?: string | null }) => {
      const label = normalizeLookupLabel(row.full_name) ?? normalizeLookupLabel(row.email) ?? row.id;
      next[row.id] = label;
    });
    setTelemetryUserLookup((prev) => ({ ...prev, ...next }));
  }, []);

  const loadTelemetryEvents = useCallback(async () => {
    setTelemetryLoading(true);
    setTelemetryError(null);
    setTelemetryFeedback({ tone: 'info', text: 'Refreshing telemetry events...' });
    try {
      const offset = telemetryPage * telemetryPageSize;
      const result = await telemetryMonitorService.getTelemetryEventsPage(telemetryPageSize, offset, {
        level: telemetryLevelFilter === 'all' ? undefined : telemetryLevelFilter,
        source: telemetrySourceFilter === 'all' ? undefined : telemetrySourceFilter,
        eventName: telemetryEventFilter === 'all' ? undefined : telemetryEventFilter,
        search: telemetrySearch.trim() || undefined,
      });
      if (result.success && result.data) {
        setTelemetryEvents(result.data);
        setTelemetryTotalCount(result.count ?? null);
        if (!result.data.length) {
          setTelemetryFeedback({ tone: 'info', text: 'No telemetry events match the current filters.' });
        } else {
          setTelemetryFeedback({
            tone: 'info',
            text: `Loaded ${result.data.length} telemetry event${result.data.length === 1 ? '' : 's'} for this page.`,
          });
        }
        await loadTelemetryUserLookup(result.data);
      } else {
        setTelemetryError(result.message || 'Failed to load telemetry events.');
        setTelemetryFeedback({ tone: 'error', text: result.message || 'Failed to load telemetry events.' });
      }
    } catch {
      setTelemetryError('Failed to load telemetry events.');
      setTelemetryFeedback({ tone: 'error', text: 'Failed to load telemetry events.' });
    } finally {
      setTelemetryLoading(false);
    }
  }, [
    loadTelemetryUserLookup,
    telemetryEventFilter,
    telemetryLevelFilter,
    telemetryPage,
    telemetryPageSize,
    telemetrySearch,
    telemetrySourceFilter,
  ]);

  const loadSessionLossSummary = useCallback(async () => {
    setSessionLossSummaryLoading(true);
    setSessionLossSummaryError(null);
    try {
      const result = await telemetryMonitorService.getSessionLossEnforcementSummary(250);
      if (result.success && result.data) {
        setSessionLossSummary(result.data);
      } else {
        setSessionLossSummary([]);
        setSessionLossSummaryError(result.message || 'Failed to load session-loss enforcement summary.');
      }
    } catch {
      setSessionLossSummary([]);
      setSessionLossSummaryError('Failed to load session-loss enforcement summary.');
    } finally {
      setSessionLossSummaryLoading(false);
    }
  }, []);

  const loadMonitoringAlerts = useCallback(async () => {
    setMonitoringAlertsLoading(true);
    setMonitoringAlertsError(null);
    setMonitoringAlertsFeedback({ tone: 'info', text: 'Refreshing monitoring alerts...' });
    try {
      const offset = monitoringAlertsPage * monitoringAlertsPageSize;
      const [alertsResult, runsResult] = await Promise.all([
        telemetryMonitorService.getAlertsPage(monitoringAlertsPageSize, offset, {
          status: monitoringAlertStatusFilter === 'all' ? undefined : monitoringAlertStatusFilter,
          severity: monitoringAlertSeverityFilter === 'all' ? undefined : monitoringAlertSeverityFilter,
        }),
        telemetryMonitorService.getRecentAlertRuns(20),
      ]);
      if (alertsResult.success && alertsResult.data) {
        setMonitoringAlerts(alertsResult.data);
        setMonitoringAlertsTotalCount(alertsResult.count ?? null);
        setMonitoringAlertsFeedback({
          tone: alertsResult.data.some((alert) => alert.status === 'open') ? 'warning' : 'info',
          text: alertsResult.data.length
            ? `Loaded ${alertsResult.data.length} alert${alertsResult.data.length === 1 ? '' : 's'} for this page.`
            : 'No monitoring alerts for the current filters.',
        });
      } else {
        setMonitoringAlertsError(alertsResult.message || 'Failed to load monitoring alerts.');
        setMonitoringAlertsFeedback({
          tone: 'error',
          text: alertsResult.message || 'Failed to load monitoring alerts.',
        });
      }
      if (runsResult.success && runsResult.data) {
        setMonitoringAlertRuns(runsResult.data);
      }
    } catch {
      setMonitoringAlertsError('Failed to load monitoring alerts.');
      setMonitoringAlertsFeedback({ tone: 'error', text: 'Failed to load monitoring alerts.' });
    } finally {
      setMonitoringAlertsLoading(false);
    }
  }, [
    monitoringAlertSeverityFilter,
    monitoringAlertStatusFilter,
    monitoringAlertsPage,
    monitoringAlertsPageSize,
  ]);

  const handleAcknowledgeAlert = useCallback(async (id: string) => {
    const result = await telemetryMonitorService.acknowledgeAlert(id);
    if (!result.success) {
      setMonitoringAlertsFeedback({ tone: 'error', text: result.message || 'Failed to acknowledge alert.' });
      return;
    }
    setMonitoringAlertsFeedback({ tone: 'info', text: 'Alert acknowledged.' });
    await loadMonitoringAlerts();
  }, [loadMonitoringAlerts]);

  const handleResolveAlert = useCallback(async (id: string) => {
    const result = await telemetryMonitorService.resolveAlert(id);
    if (!result.success) {
      setMonitoringAlertsFeedback({ tone: 'error', text: result.message || 'Failed to resolve alert.' });
      return;
    }
    setMonitoringAlertsFeedback({ tone: 'info', text: 'Alert resolved.' });
    await loadMonitoringAlerts();
  }, [loadMonitoringAlerts]);

  const loadEditLogs = useCallback(async () => {
    setEditLogsLoading(true);
    setEditLogsError(null);
    try {
      const [logResult, householdResult, categoryResult] = await Promise.all([
        supabase
          .from('user_edit_logs')
          .select(
          `id, created_at, source_table, action, entity_id, actor_name, actor_email, actor_roles, changes, user:users!user_edit_logs_user_id_fkey(id, full_name, email), actor:users!user_edit_logs_actor_id_fkey(id, full_name, email)`
          )
          .order('created_at', { ascending: false })
          .limit(200),
        supabase.from('households').select('id, name').order('name', { ascending: true }).limit(500),
        supabase.from('volunteer_categories').select('key, label').order('label', { ascending: true }).limit(200),
      ]);

      if (logResult.error) {
        throw logResult.error;
      }

      const rawRows = (logResult.data ?? []) as Array<
        UserEditLog & {
          user?: EditLogUserRef | EditLogUserRef[] | null;
          actor?: EditLogUserRef | EditLogUserRef[] | null;
        }
      >;
      const normalized = rawRows.map((row) => ({
        ...row,
        user: Array.isArray(row.user) ? row.user[0] ?? null : row.user ?? null,
        actor: Array.isArray(row.actor) ? row.actor[0] ?? null : row.actor ?? null,
        changes:
          row.changes && typeof row.changes === 'object' && !Array.isArray(row.changes)
            ? row.changes
            : {},
      }));
      setEditLogs(normalized);

      if (!householdResult.error) {
        const next: Record<string, string> = {};
        (householdResult.data ?? []).forEach((row: { id: string; name?: string | null }) => {
          const label = normalizeLookupLabel(row.name);
          if (label) {
            next[row.id] = label;
          }
        });
        setHouseholdLookup(next);
      }

      if (!categoryResult.error) {
        const next: Record<string, string> = {};
        (categoryResult.data ?? []).forEach((row: { key: string; label?: string | null }) => {
          const label = normalizeLookupLabel(row.label) ?? row.key;
          if (label) {
            next[row.key] = label;
          }
        });
        setVolunteerCategoryLookup(next);
      }
    } catch (error) {
      setEditLogsError('Failed to load edit log.');
    } finally {
      setEditLogsLoading(false);
    }
  }, []);


  const editLogSources = useMemo(() => {
    const sources = new Set<string>();
    editLogs.forEach((log) => {
      if (log.source_table) {
        sources.add(log.source_table);
      }
    });
    return Array.from(sources).sort((a, b) => a.localeCompare(b));
  }, [editLogs]);

  const formatChangeValueForLog = useCallback(
    (field: string, value: unknown, log: UserEditLog, targetLabel: string, actorLabel: string) => {
      if (field === 'category_key' && typeof value === 'string') {
        return volunteerCategoryLookup[value] ?? value;
      }
      if (field === 'household_id' && typeof value === 'string') {
        return householdLookup[value] ?? value;
      }
      if (typeof value === 'string') {
        if (value === log.user?.id) return targetLabel;
        if (value === log.actor?.id) return actorLabel;
        if (isUuid(value)) {
          return `ID ${value.slice(0, 8)}`;
        }
      }
      return formatChangeValue(value);
    },
    [householdLookup, volunteerCategoryLookup],
  );

  const filteredEditLogs = useMemo(() => {
    const normalizedSearch = editLogSearch.trim().toLowerCase();
    const fromDate = editLogFromDate ? new Date(editLogFromDate) : null;
    const toDate = editLogToDate ? new Date(editLogToDate) : null;
    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return editLogs.filter((log) => {
      if (editLogSourceFilter !== 'all' && log.source_table !== editLogSourceFilter) {
        return false;
      }

      const logDate = new Date(log.created_at);
      if (fromDate && logDate < fromDate) return false;
      if (toDate && logDate > toDate) return false;

      if (!normalizedSearch) return true;

      const targetLabel =
        log.user?.full_name || log.user?.email || log.user?.id || 'Unknown user';
      const actorLabel =
        log.actor_name ||
        log.actor_email ||
        log.actor?.full_name ||
        log.actor?.email ||
        log.actor?.id ||
        'System';
      const changeText = Object.entries(log.changes ?? {})
        .map(([field, values]) => {
          const oldValue = formatChangeValueForLog(field, values?.old, log, targetLabel, actorLabel);
          const newValue = formatChangeValueForLog(field, values?.new, log, targetLabel, actorLabel);
          return `${field}: ${oldValue} -> ${newValue}`;
        })
        .join(' ');
      const combined = `${targetLabel} ${actorLabel} ${log.source_table} ${log.action} ${changeText}`.toLowerCase();
      return combined.includes(normalizedSearch);
    });
  }, [
    editLogs,
    editLogSourceFilter,
    editLogSearch,
    editLogFromDate,
    editLogToDate,
    formatChangeValueForLog,
  ]);

  const groupedEditLogs = useMemo(() => {
    if (!groupEditEvents) return filteredEditLogs;
    const grouped: Array<UserEditLog & { event_count?: number }> = [];
    const groupWindowMs = 5 * 60 * 1000;

    filteredEditLogs.forEach((log) => {
      const lastGroup = grouped[grouped.length - 1];
      if (!lastGroup) {
        grouped.push({ ...log, event_count: 1 });
        return;
      }

      const lastGroupTime = new Date(lastGroup.created_at).getTime();
      const logTime = new Date(log.created_at).getTime();
      const sameActor =
        (log.actor?.id || log.actor_name || log.actor_email || null) ===
        (lastGroup.actor?.id || lastGroup.actor_name || lastGroup.actor_email || null);
      const sameTarget =
        (log.user?.id || null) === (lastGroup.user?.id || null);
      const sameSource = log.source_table === lastGroup.source_table;

      if (sameActor && sameTarget && sameSource && lastGroupTime - logTime <= groupWindowMs) {
        lastGroup.event_count = (lastGroup.event_count ?? 1) + 1;
        Object.entries(log.changes ?? {}).forEach(([field, values]) => {
          if (!lastGroup.changes[field]) {
            lastGroup.changes[field] = { old: values?.old, new: values?.new };
          } else {
            lastGroup.changes[field] = {
              old: values?.old ?? lastGroup.changes[field].old,
              new: lastGroup.changes[field].new,
            };
          }
        });
        return;
      }

      grouped.push({ ...log, event_count: 1 });
    });

    return grouped;
  }, [filteredEditLogs, groupEditEvents]);

  const formatChangeDiff = useCallback(
    (field: string, oldValue: unknown, newValue: unknown, log: UserEditLog, targetLabel: string, actorLabel: string) => {
      if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        const oldSet = new Set(oldValue.map((value) => String(value)));
        const newSet = new Set(newValue.map((value) => String(value)));
        const added = newValue.filter((value) => !oldSet.has(String(value)));
        const removed = oldValue.filter((value) => !newSet.has(String(value)));
        const parts: string[] = [];
        if (added.length) {
          parts.push(
            `+ ${added
              .map((value) => formatChangeValueForLog(field, value, log, targetLabel, actorLabel))
              .join(', ')}`
          );
        }
        if (removed.length) {
          parts.push(
            `- ${removed
              .map((value) => formatChangeValueForLog(field, value, log, targetLabel, actorLabel))
              .join(', ')}`
          );
        }
        return parts.length ? parts.join('; ') : 'No changes';
      }

      const oldFormatted = formatChangeValueForLog(field, oldValue, log, targetLabel, actorLabel);
      const newFormatted = formatChangeValueForLog(field, newValue, log, targetLabel, actorLabel);
      return `${oldFormatted} -> ${newFormatted}`;
    },
    [formatChangeValueForLog],
  );

  const handleExportEditLogs = useCallback(() => {
    if (!filteredEditLogs.length) return;
    const escapeCsvValue = (value: string) => {
      const escaped = value.replace(/"/g, '""');
      return `"${escaped}"`;
    };
    const rows = filteredEditLogs.map((log) => {
      const targetLabel =
        log.user?.full_name || log.user?.email || log.user?.id || 'Unknown user';
      const actorLabel =
        log.actor_name ||
        log.actor_email ||
        log.actor?.full_name ||
        log.actor?.email ||
        log.actor?.id ||
        'System';
      const actorRoles = log.actor_roles?.length
        ? log.actor_roles.map((role) => getRoleLabel(role)).join(', ')
        : '';
      const changeEntries = Object.entries(log.changes ?? {})
        .filter(([field]) => !shouldHideChangeField(field))
        .map(([field, values]) => `${field}: ${formatChangeDiff(field, values?.old, values?.new, log, targetLabel, actorLabel)}`)
        .join('; ');
      return [
        new Date(log.created_at).toLocaleString(),
        formatSourceLabel(log.source_table, log.action),
        targetLabel,
        actorLabel,
        actorRoles,
        changeEntries,
      ].map((value) => escapeCsvValue(String(value ?? '')));
    });
    const header = ['When', 'Type', 'User', 'Edited By', 'Actor Roles', 'Changes'].map(escapeCsvValue).join(',');
    const csv = [header, ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user-edit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredEditLogs, formatChangeDiff]);

  const handleTogglePushRole = useCallback(
    (notificationKey: string, role: string) => {
      setPushNotificationRoleConfig((prev) => {
        const currentConfig = prev ?? { ...DEFAULT_PUSH_NOTIFICATION_ROLE_CONFIG };
        const currentRoles = Array.isArray(currentConfig[notificationKey])
          ? currentConfig[notificationKey]
          : [];
        const nextRoles = currentRoles.includes(role)
          ? currentRoles.filter((item) => item !== role)
          : [...currentRoles, role];

        return { ...currentConfig, [notificationKey]: nextRoles };
      });
      setPushNotificationRolesDirty(true);
    },
    []
  );

  const handleSavePushRoles = useCallback(async () => {
    if (!pushNotificationRoleConfig) return;
    setPushNotificationRolesSaving(true);
    try {
      const result = await appConfigService.updatePushNotificationRoleConfig(pushNotificationRoleConfig);
      if (!result.success) {
        throw new Error(result.message || 'Failed to update push notification roles.');
      }
      setPushNotificationRolesDirty(false);
      showMessage('success', 'Push notification roles updated.');
    } catch (error: any) {
      showMessage('error', error?.message || 'Failed to update push notification roles.');
    } finally {
      setPushNotificationRolesSaving(false);
    }
  }, [pushNotificationRoleConfig, showMessage]);

  const performanceStats = useMemo(() => {
    if (!performanceSnapshots.length) return null;
    const samples = performanceSnapshots.slice(0, 50);
    const avgActive =
      samples.reduce((sum, row) => sum + toNumberSafe(row.active_operations), 0) / samples.length;
    const avgPending =
      samples.reduce((sum, row) => sum + toNumberSafe(row.pending_operations), 0) / samples.length;
    const avgQueue =
      samples.reduce((sum, row) => sum + toNumberSafe(row.upload_queue_depth), 0) / samples.length;
    const avgChannels =
      samples.reduce((sum, row) => sum + toNumberSafe(row.realtime_channels), 0) / samples.length;
    const connectedRate =
      samples.filter((row) => row.ws_connected).length / samples.length;

    const series = (key: keyof PerformanceSnapshot) =>
      samples
        .slice()
        .reverse()
        .map((row) => toNumberSafe(row[key] as number | null));

    return {
      avgActive,
      avgPending,
      avgQueue,
      avgChannels,
      connectedRate,
      activeSeries: series('active_operations'),
      pendingSeries: series('pending_operations'),
      queueSeries: series('upload_queue_depth'),
      channelsSeries: series('realtime_channels'),
    };
  }, [performanceSnapshots]);

  const performanceDerivedFeedback = useMemo<PerfFeedbackState | null>(() => {
    if (performanceError) {
      return { tone: 'error', text: performanceError };
    }
    if (performanceLoading) {
      return { tone: 'info', text: 'Loading performance snapshots...' };
    }
    if (!performanceSnapshots.length) {
      return {
        tone: 'info',
        text: 'No performance snapshots recorded yet. Open the mobile app and trigger activity to populate this table.',
      };
    }
    if (!performanceStats) {
      return null;
    }
    const warnings: string[] = [];
    if (performanceStats.connectedRate < 0.9) {
      warnings.push(`Realtime connected rate is ${(performanceStats.connectedRate * 100).toFixed(0)}% on this page.`);
    }
    if (performanceStats.avgPending >= 5) {
      warnings.push(`Average pending operations is ${performanceStats.avgPending.toFixed(1)}.`);
    }
    if (performanceStats.avgQueue >= 3) {
      warnings.push(`Average upload queue depth is ${performanceStats.avgQueue.toFixed(1)}.`);
    }
    if (!warnings.length) {
      return {
        tone: 'info',
        text: 'Performance signals look stable for the current page of snapshots.',
      };
    }
    return { tone: 'warning', text: warnings.join(' ') };
  }, [performanceError, performanceLoading, performanceSnapshots.length, performanceStats]);

  const appErrorStats = useMemo(() => {
    if (!appErrorEvents.length) return null;
    const totals = appErrorEvents.reduce(
      (acc, event) => {
        acc.total += 1;
        if (event.level === 'error') acc.errors += 1;
        if (event.level === 'warning') acc.warnings += 1;
        if (event.level === 'info') acc.infos += 1;
        return acc;
      },
      { total: 0, errors: 0, warnings: 0, infos: 0 },
    );
    return totals;
  }, [appErrorEvents]);

  const telemetryStats = useMemo(() => {
    if (!telemetryEvents.length) return null;
    return telemetryEvents.reduce(
      (acc, event) => {
        acc.total += 1;
        if (event.level === 'error') acc.errors += 1;
        if (event.level === 'warning') acc.warnings += 1;
        if (event.level === 'info') acc.infos += 1;
        return acc;
      },
      { total: 0, errors: 0, warnings: 0, infos: 0 },
    );
  }, [telemetryEvents]);

  const telemetrySourceOptions = useMemo(() => {
    const sources = new Set<string>();
    telemetryEvents.forEach((event) => {
      if (event.source) sources.add(event.source);
    });
    return Array.from(sources).sort((a, b) => a.localeCompare(b));
  }, [telemetryEvents]);

  const telemetryEventOptions = useMemo(() => {
    const names = new Set<string>();
    telemetryEvents.forEach((event) => {
      if (event.event_name) names.add(event.event_name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [telemetryEvents]);

  const sessionLossSummaryTotal = useMemo(
    () => sessionLossSummary.reduce((sum, entry) => sum + entry.count, 0),
    [sessionLossSummary],
  );

  const monitoringCoverageStats = useMemo(() => {
    const now = Date.now();
    const snapshot24h = performanceSnapshots.filter(
      (snapshot) => now - new Date(snapshot.created_at).getTime() <= 24 * 60 * 60 * 1000
    );
    const error24h = appErrorEvents.filter(
      (event) => now - new Date(event.created_at).getTime() <= 24 * 60 * 60 * 1000
    );
    const telemetry24h = telemetryEvents.filter(
      (event) => now - new Date(event.created_at).getTime() <= 24 * 60 * 60 * 1000
    );

    const lastSeen: Record<string, string | null> = {
      snapshots: performanceSnapshots[0]?.created_at ?? null,
      errors: appErrorEvents[0]?.created_at ?? null,
      telemetry: telemetryEvents[0]?.created_at ?? null,
    };
    return {
      snapshots24h: snapshot24h.length,
      errors24h: error24h.length,
      telemetry24h: telemetry24h.length,
      lastSeen,
    };
  }, [appErrorEvents, performanceSnapshots, telemetryEvents]);

  const telemetryDerivedFeedback = useMemo<PerfFeedbackState | null>(() => {
    if (telemetryError) {
      return { tone: 'error', text: telemetryError };
    }
    if (telemetryLoading) {
      return { tone: 'info', text: 'Loading telemetry events...' };
    }
    if (!telemetryEvents.length) {
      return { tone: 'info', text: 'No telemetry events recorded for the current filters.' };
    }
    if (!telemetryStats) {
      return null;
    }
    if (telemetryStats.errors > 0 || telemetryStats.warnings > 0) {
      return {
        tone: 'warning',
        text: `This page includes ${telemetryStats.errors} error(s), ${telemetryStats.warnings} warning(s), and ${telemetryStats.infos} info telemetry event(s).`,
      };
    }
    return { tone: 'info', text: `Loaded ${telemetryStats.infos} info telemetry event(s).` };
  }, [telemetryError, telemetryEvents.length, telemetryLoading, telemetryStats]);

  const appErrorDerivedFeedback = useMemo<PerfFeedbackState | null>(() => {
    if (appErrorError) {
      return { tone: 'error', text: appErrorError };
    }
    if (appErrorLoading) {
      return { tone: 'info', text: 'Loading app error events...' };
    }
    if (!appErrorEvents.length) {
      return {
        tone: 'info',
        text: 'No app error events recorded for the current filters.',
      };
    }
    if (!appErrorStats) {
      return null;
    }
    if (appErrorStats.errors > 0 || appErrorStats.warnings > 0) {
      return {
        tone: 'warning',
        text: `This page includes ${appErrorStats.errors} error(s), ${appErrorStats.warnings} warning(s), and ${appErrorStats.infos} info event(s).`,
      };
    }
    return {
      tone: 'info',
      text: `Only info-level events are present on this page (${appErrorStats.infos} total).`,
    };
  }, [appErrorError, appErrorEvents.length, appErrorLoading, appErrorStats]);

  const performanceFeedbackToShow = performanceFeedback ?? performanceDerivedFeedback;
  const appErrorFeedbackToShow = appErrorFeedback ?? appErrorDerivedFeedback;
  const telemetryFeedbackToShow = telemetryFeedback ?? telemetryDerivedFeedback;

  const getAppErrorUserLabel = useCallback(
    (userId?: string | null) => {
      if (!userId) return '';
      return appErrorUserLookup[userId] ?? compactId(userId);
    },
    [appErrorUserLookup]
  );

  const getPerformanceUserLabel = useCallback(
    (userId?: string | null) => {
      if (!userId) return '';
      return performanceUserLookup[userId] ?? compactId(userId);
    },
    [performanceUserLookup]
  );

  const getTelemetryUserLabel = useCallback(
    (userId?: string | null) => {
      if (!userId) return '';
      return telemetryUserLookup[userId] ?? compactId(userId);
    },
    [telemetryUserLookup]
  );

  useEffect(() => {
    if (isSuperAdmin) {
      loadAllConfigurations();
    }
  }, [isSuperAdmin, loadAllConfigurations]);

  useEffect(() => {
    if (isSuperAdmin && activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, isSuperAdmin, loadAnalytics]);

  useEffect(() => {
    if (isSuperAdmin && activeTab === 'performance') {
      loadPerformanceSnapshots();
      loadAppErrorEvents();
      loadTelemetryEvents();
      loadSessionLossSummary();
      loadMonitoringAlerts();
    }
  }, [activeTab, isSuperAdmin, loadPerformanceSnapshots, loadAppErrorEvents, loadTelemetryEvents, loadSessionLossSummary, loadMonitoringAlerts]);

  useEffect(() => {
    const sessionId = selectedRealtimeSnapshot?.session_id;
    if (!sessionId) {
      setCorrelatedTelemetryEvents([]);
      setCorrelatedAppErrorEvents([]);
      setCorrelatedEventsLoading(false);
      return;
    }
    let cancelled = false;
    const loadCorrelated = async () => {
      setCorrelatedEventsLoading(true);
      try {
        const [telemetryResult, errorsResult] = await Promise.all([
          supabase
            .from('app_telemetry_events')
            .select('id, created_at, user_id, session_id, level, event_name, message, source, platform, app_version, url, user_agent, tags, payload, context')
            .eq('session_id', sessionId)
            .order('created_at', { ascending: false })
            .limit(30),
          supabase
            .from('app_error_events')
            .select('id, created_at, user_id, session_id, level, message, stack, source, url, user_agent, platform, app_version, tags, context')
            .eq('session_id', sessionId)
            .order('created_at', { ascending: false })
            .limit(30),
        ]);
        if (cancelled) return;
        setCorrelatedTelemetryEvents((telemetryResult.data ?? []) as TelemetryEventRecord[]);
        setCorrelatedAppErrorEvents((errorsResult.data ?? []) as AppErrorEvent[]);
      } finally {
        if (!cancelled) {
          setCorrelatedEventsLoading(false);
        }
      }
    };
    void loadCorrelated();
    return () => {
      cancelled = true;
    };
  }, [selectedRealtimeSnapshot?.session_id]);

  useEffect(() => {
    if (isSuperAdmin && activeTab === 'edit-log') {
      loadEditLogs();
    }
  }, [activeTab, isSuperAdmin, loadEditLogs]);


  const handleSave = async (key: string, action: () => Promise<boolean>) => {
    setSavingKey(key);
    try {
      const ok = await action();
      showMessage(ok ? 'success' : 'error', ok ? 'Saved successfully.' : 'Failed to save.');
    } catch (error) {
      showMessage('error', 'Something went wrong while saving.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleUploadAsset = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const type = inferAssetType(file.type);
      const result = await appConfigService.uploadAsset(file.name, type, file, {});
      if (result.success) {
        showMessage('success', 'Asset uploaded successfully.');
        const refreshed = await appConfigService.getAllAssets();
        if (refreshed.success && refreshed.data) setAssets(refreshed.data);
      } else {
        showMessage('error', result.message || 'Failed to upload asset.');
      }
    } catch (error) {
      showMessage('error', 'Error uploading asset.');
    } finally {
      setUploading(false);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
    }
  };

  const handleUploadHomeLogo = async (file?: File | null) => {
    if (!file || !appSettings) return;
    const maxUploadBytes = (appSettings.max_file_upload_size || 5) * 1024 * 1024;
    if (file.size > maxUploadBytes) {
      showMessage('error', `Please upload an image smaller than ${appSettings.max_file_upload_size || 5}MB.`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please upload an image file for the home logo.');
      return;
    }

    const allowedTypes = (appSettings.allowed_file_types || []).map((value) => value.toLowerCase());
    if (allowedTypes.length) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isAllowed =
        (fileExtension && allowedTypes.includes(fileExtension)) ||
        allowedTypes.includes(file.type.toLowerCase()) ||
        allowedTypes.includes('image/*') ||
        allowedTypes.includes('image');
      if (!isAllowed) {
        showMessage('error', `File type not allowed. Allowed: ${allowedTypes.join(', ')}.`);
        return;
      }
    }

    setUploadingHomeLogo(true);
    try {
      const result = await appConfigService.uploadAsset(file.name, 'image', file, { purpose: 'home_logo' });
      if (result.success && result.data) {
        const asset = result.data;
        const url = appConfigService.getAssetUrl(asset.file_path);
        setAppSettings((prev) => (prev ? { ...prev, home_logo_url: url } : prev));
        setLogoPrefilled(true);
        setAssets((prev) => [asset, ...prev]);
        showMessage('success', 'Home logo uploaded. Click save to apply.');
      } else {
        showMessage('error', result.message || 'Failed to upload home logo.');
      }
    } catch (error) {
      showMessage('error', 'Error uploading home logo.');
    } finally {
      setUploadingHomeLogo(false);
      if (homeLogoInputRef.current) {
        homeLogoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    const confirmed = window.confirm('Delete this asset? This action cannot be undone.');
    if (!confirmed) return;
    const result = await appConfigService.deleteAsset(assetId);
    if (result.success) {
      setAssets((prev) => prev.filter((asset) => asset.id !== assetId));
      showMessage('success', 'Asset deleted.');
    } else {
      showMessage('error', result.message || 'Failed to delete asset.');
    }
  };

  const themeEntries = useMemo(() => (themeColors ? Object.entries(themeColors) : []), [themeColors]);
  const quickActionEntries = useMemo(
    () =>
      QUICK_ACTION_DEFINITIONS.map((definition) => ({
        key: definition.id,
        title: definition.title,
        enabled: quickActions?.[definition.id]?.enabled !== false,
      })),
    [quickActions]
  );
  const headerEntries = useMemo(() => (headerImages ? Object.entries(headerImages) : []), [headerImages]);
  const inferredLogoUrl = useMemo(() => {
    if (!assets.length) return null;
    const candidate =
      assets.find((asset) => asset.name.toLowerCase().includes('logo')) ?? assets[0];
    return candidate ? appConfigService.getAssetUrl(candidate.file_path) : null;
  }, [assets]);

  const renderAnalyticsMetrics = (data?: Record<string, unknown> | null) => {
    if (!data) return <EmptyState>No metrics available.</EmptyState>;
    const entries = Object.entries(data);
    if (!entries.length) return <EmptyState>No metrics available.</EmptyState>;
    return (
      <AnalyticsMetricsGrid>
        {entries.map(([key, value]) => {
          const isWide =
            Array.isArray(value) && value.some((item) => item && typeof item === 'object');
          return (
            <AnalyticsMetricTile key={key} $full={isWide}>
              <AnalyticsMetricName>{formatMetricLabel(key)}</AnalyticsMetricName>
              <AnalyticsMetricBody>{renderMetricValue(value)}</AnalyticsMetricBody>
            </AnalyticsMetricTile>
          );
        })}
      </AnalyticsMetricsGrid>
    );
  };

  useEffect(() => {
    if (logoPrefilled) return;
    if (!appSettings) return;
    if (appSettings.home_logo_url) {
      setLogoPrefilled(true);
      return;
    }
    if (inferredLogoUrl) {
      setAppSettings({ ...appSettings, home_logo_url: inferredLogoUrl });
      setLogoPrefilled(true);
    }
  }, [appSettings, inferredLogoUrl, logoPrefilled]);

  if (isLoading || loading) {
    return (
      <Page>
        <LoadingText>Loading</LoadingText>
      </Page>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Page>
        <Card>
          <CardTitle>Access Denied</CardTitle>
          <CardSubtitle>Only Super Admins can access this panel.</CardSubtitle>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderRow>
        <PageTitle>Super Admin</PageTitle>
      </HeaderRow>

      <Tabs>
        {SUPER_ADMIN_TABS.map((tab) => (
          <TabButton key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </TabButton>
        ))}
      </Tabs>

      {message && <Message $type={message.type}>{message.text}</Message>}

      {activeTab === 'theme' && themeColors && (
        <Card>
          <CardTitle>Theme Colors</CardTitle>
          <CardSubtitle>Customize the apps color scheme.</CardSubtitle>
          <Grid>
            {themeEntries.map(([key, value]) => (
              <div key={key}>
                <Label>{key}</Label>
                <InlineRow>
                  <TextInput
                    value={value}
                    onChange={(event) =>
                      setThemeColors({ ...themeColors, [key]: event.target.value })
                    }
                  />
                  <input
                    type="color"
                    value={value}
                    onChange={(event) =>
                      setThemeColors({ ...themeColors, [key]: event.target.value })
                    }
                  />
                </InlineRow>
              </div>
            ))}
          </Grid>
          <Divider />
          <SaveButton
            disabled={savingKey === 'theme'}
            onClick={() =>
              handleSave('theme', async () => {
                if (!themeColors) return false;
                const result = await appConfigService.updateThemeColors(themeColors);
                return result.success;
              })
            }
          >
            Save Theme Colors
          </SaveButton>
        </Card>
      )}

      {activeTab === 'quick-actions' && quickActions && (
        <Card>
          <CardTitle>Quick Actions</CardTitle>
          <CardSubtitle>
            Same tiles as the mobile home screen. Toggle Enabled to show or hide each action.
          </CardSubtitle>
          <QuickActionsGrid>
            {quickActionEntries.map(({ key, title, enabled }) => {
              const previewUrl = getQuickActionAdminPreviewUrl(key, quickActions[key]?.backgroundImage);
              const showImage = Boolean(previewUrl && !quickActionImageFailed[key]);
              return (
                <QuickActionTile key={key} $dimmed={!enabled}>
                  {showImage ? (
                    <QuickActionTileImage
                      src={previewUrl!}
                      alt=""
                      loading="lazy"
                      onError={() =>
                        setQuickActionImageFailed((prev) => (prev[key] ? prev : { ...prev, [key]: true }))
                      }
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.75rem',
                        textAlign: 'center',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: 'var(--onSurfaceVariant)',
                      }}
                    >
                      {title}
                    </div>
                  )}
                  <QuickActionTileBar>
                    <QuickActionTileTitle>{title}</QuickActionTileTitle>
                    <QuickActionTileToggleWrap>
                      <Toggle
                        type="checkbox"
                        checked={enabled}
                        aria-label={`Enable ${title}`}
                        onChange={(event) =>
                          setQuickActions((prev) => {
                            if (!prev) return prev;
                            const definition = QUICK_ACTION_DEFINITIONS.find((item) => item.id === key);
                            const current = prev[key];

                            return {
                              ...prev,
                              [key]: {
                                title: current?.title ?? definition?.title ?? key,
                                icon: current?.icon ?? definition?.icon ?? '',
                                color: current?.color ?? '',
                                action: current?.action ?? key,
                                backgroundImage: current?.backgroundImage,
                                enabled: event.target.checked,
                              },
                            };
                          })
                        }
                      />
                    </QuickActionTileToggleWrap>
                  </QuickActionTileBar>
                </QuickActionTile>
              );
            })}
          </QuickActionsGrid>
          <Divider />
          <SaveButton
            disabled={savingKey === 'quick-actions'}
            onClick={() =>
              handleSave('quick-actions', async () => {
                if (!quickActions) return false;
                const result = await appConfigService.updateQuickActions(quickActions);
                return result.success;
              })
            }
          >
            Save Quick Actions
          </SaveButton>
        </Card>
      )}

      {activeTab === 'headers' && headerImages && (
        <Card>
          <CardTitle>Header Images</CardTitle>
          <CardSubtitle>Configure header styles for different screens.</CardSubtitle>
          <Section>
            {headerEntries.map(([key, header]) => (
              <Card key={key}>
                <CardTitle style={{ fontSize: '1rem' }}>{key}</CardTitle>
                <Section>
                  <FieldRow>
                    <Label>Enabled</Label>
                    <Toggle
                      type="checkbox"
                      checked={header.enabled}
                      onChange={(event) =>
                        setHeaderImages({
                          ...headerImages,
                          [key]: { ...header, enabled: event.target.checked },
                        })
                      }
                    />
                  </FieldRow>
                  <FieldRow>
                    <Label>Type</Label>
                    <Select
                      value={header.type}
                      onChange={(event) =>
                        setHeaderImages({
                          ...headerImages,
                          [key]: { ...header, type: event.target.value as 'gradient' | 'image' },
                        })
                      }
                    >
                      <option value="gradient">Gradient</option>
                      <option value="image">Image</option>
                    </Select>
                  </FieldRow>

                  {header.type === 'gradient' && (
                    <Section>
                      {(header.gradient_colors ?? []).map((color, index) => (
                        <FieldRow key={`${key}-gradient-${index}`}>
                          <Label>Gradient Color {index + 1}</Label>
                          <InlineRow>
                            <TextInput
                              value={color}
                              onChange={(event) => {
                                const updated = [...(header.gradient_colors ?? [])];
                                updated[index] = event.target.value;
                                setHeaderImages({
                                  ...headerImages,
                                  [key]: { ...header, gradient_colors: updated },
                                });
                              }}
                            />
                            <input
                              type="color"
                              value={color}
                              onChange={(event) => {
                                const updated = [...(header.gradient_colors ?? [])];
                                updated[index] = event.target.value;
                                setHeaderImages({
                                  ...headerImages,
                                  [key]: { ...header, gradient_colors: updated },
                                });
                              }}
                            />
                          </InlineRow>
                        </FieldRow>
                      ))}
                    </Section>
                  )}

                  {header.type === 'image' && (
                    <FieldRow>
                      <Label>Image URL</Label>
                      <TextInput
                        value={header.image_url ?? ''}
                        onChange={(event) =>
                          setHeaderImages({
                            ...headerImages,
                            [key]: { ...header, image_url: event.target.value },
                          })
                        }
                      />
                    </FieldRow>
                  )}
                </Section>
              </Card>
            ))}
          </Section>
          <Divider />
          <SaveButton
            disabled={savingKey === 'headers'}
            onClick={() =>
              handleSave('headers', async () => {
                if (!headerImages) return false;
                const result = await appConfigService.updateHeaderImages(headerImages);
                return result.success;
              })
            }
          >
            Save Header Images
          </SaveButton>
        </Card>
      )}

      {activeTab === 'store' && (
        <Card>
          <CardTitle>Store</CardTitle>
          <CardSubtitle>
            Manage the shared product catalog, storefront settings, and incoming orders in the dedicated store admin surface.
          </CardSubtitle>
          <Section>
            <SmallText>
              Mobile and website store products now live in the shared store tables. Catalog uploads and order management should happen in the dedicated store page, not inside App Settings.
            </SmallText>
            <StoreAdminActions>
              <StoreAdminLink href="/admin/website/store">Open Store Management</StoreAdminLink>
              <StoreAdminLink href="/admin/website" $secondary>
                Open Website Admin
              </StoreAdminLink>
            </StoreAdminActions>
          </Section>
        </Card>
      )}

      {activeTab === 'settings' && appSettings && (
        <Card>
          <CardTitle>App Settings</CardTitle>
          <CardSubtitle>General application settings.</CardSubtitle>
          <Section>
            <FieldRow>
              <Label>App Name</Label>
              <TextInput
                value={appSettings.app_name}
                onChange={(event) => setAppSettings({ ...appSettings, app_name: event.target.value })}
              />
            </FieldRow>
            <FieldRow>
              <Label>App Version</Label>
              <TextInput
                value={appSettings.app_version}
                onChange={(event) => setAppSettings({ ...appSettings, app_version: event.target.value })}
              />
            </FieldRow>
            <FieldRow>
              <Label>Latest iOS Version</Label>
              <TextInput
                value={appSettings.latest_ios_build}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, latest_ios_build: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Latest Android Version</Label>
              <TextInput
                value={appSettings.latest_android_build}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, latest_android_build: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>iOS Store URL</Label>
              <TextInput
                value={appSettings.ios_store_url}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, ios_store_url: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Android Store URL</Label>
              <TextInput
                value={appSettings.android_store_url}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, android_store_url: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Update Prompt Title</Label>
              <TextInput
                value={appSettings.update_prompt_title}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, update_prompt_title: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Update Prompt Message</Label>
              <TextArea
                value={appSettings.update_prompt_message}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, update_prompt_message: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Home Header Logo</Label>
              <Section>
                <InlineRow>
                  <UploadButton
                    type="button"
                    onClick={() => homeLogoInputRef.current?.click()}
                    disabled={uploadingHomeLogo}
                  >
                    {uploadingHomeLogo ? 'Uploading...' : 'Upload Logo'}
                  </UploadButton>
                  <input
                    ref={homeLogoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => handleUploadHomeLogo(event.target.files?.[0])}
                  />
                  {appSettings.home_logo_url ? (
                    <ExternalLink href={appSettings.home_logo_url} target="_blank" rel="noreferrer">
                      View current logo
                    </ExternalLink>
                  ) : (
                    <SmallText>No logo uploaded yet.</SmallText>
                  )}
                </InlineRow>
                <InlineRow>
                  <TextInput
                    value={appSettings.home_logo_url ?? ''}
                    readOnly
                    placeholder="Upload to set the logo URL"
                  />
                  {appSettings.home_logo_url ? (
                    <img
                      src={appSettings.home_logo_url}
                      alt="Home logo preview"
                      style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8 }}
                    />
                  ) : null}
                </InlineRow>
              </Section>
            </FieldRow>
            <FieldRow>
              <Label>Default Language</Label>
              <TextInput
                value={appSettings.default_language}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, default_language: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Supported Languages</Label>
              <TextInput
                value={listToString(appSettings.supported_languages)}
                onChange={(event) =>
                  setAppSettings({
                    ...appSettings,
                    supported_languages: stringToList(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Max Upload Size (MB)</Label>
              <TextInput
                value={appSettings.max_file_upload_size.toString()}
                onChange={(event) =>
                  setAppSettings({
                    ...appSettings,
                    max_file_upload_size: toNumber(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Allowed File Types</Label>
              <TextInput
                value={listToString(appSettings.allowed_file_types)}
                onChange={(event) =>
                  setAppSettings({
                    ...appSettings,
                    allowed_file_types: stringToList(event.target.value),
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Maintenance Mode</Label>
              <Toggle
                type="checkbox"
                checked={appSettings.maintenance_mode}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, maintenance_mode: event.target.checked })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Maintenance Message</Label>
              <TextArea
                value={appSettings.maintenance_message}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, maintenance_message: event.target.value })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Push Notifications</Label>
              <Toggle
                type="checkbox"
                checked={appSettings.push_notifications_enabled}
                onChange={(event) =>
                  setAppSettings({
                    ...appSettings,
                    push_notifications_enabled: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Analytics Enabled</Label>
              <Toggle
                type="checkbox"
                checked={appSettings.analytics_enabled}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, analytics_enabled: event.target.checked })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Debug Mode</Label>
              <Toggle
                type="checkbox"
                checked={appSettings.debug_mode}
                onChange={(event) =>
                  setAppSettings({ ...appSettings, debug_mode: event.target.checked })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>App Roadmap</Label>
              <Section>
                <CardSubtitle style={{ marginBottom: 8 }}>
                  Bulleted list shown on the mobile app home screen below Spotlight. One item per line.
                </CardSubtitle>
                <TextArea
                  value={(appSettings.app_roadmap ?? []).join('\n')}
                  onChange={(event) =>
                    setAppSettings({
                      ...appSettings,
                      app_roadmap: event.target.value.split('\n'),
                    })
                  }
                  placeholder={'e.g.\nNew giving flow\nKids check-in v2\nEvent reminders'}
                  rows={6}
                />
              </Section>
            </FieldRow>
          </Section>
          <Divider />
          <SaveButton
            disabled={savingKey === 'settings'}
            onClick={() =>
              handleSave('settings', async () => {
                if (!appSettings) return false;
                const settingsToSave = {
                  ...appSettings,
                  app_roadmap: (appSettings.app_roadmap ?? [])
                    .map((s) => s.trim())
                    .filter(Boolean),
                };
                const result = await appConfigService.updateAppSettings(settingsToSave);
                return result.success;
              })
            }
          >
            Save App Settings
          </SaveButton>
        </Card>
      )}

      {activeTab === 'features' && featureFlags && (
        <Card>
          <CardTitle>Feature Flags</CardTitle>
          <CardSubtitle>Enable or disable app features.</CardSubtitle>
          <Section>
            {Object.entries(featureFlags).map(([key, enabled]) => (
              <FieldRow key={key}>
                <Label>{key.replace(/_/g, ' ')}</Label>
                <Toggle
                  type="checkbox"
                  checked={enabled}
                  onChange={(event) =>
                    setFeatureFlags({ ...featureFlags, [key]: event.target.checked })
                  }
                />
              </FieldRow>
            ))}
          </Section>
          <Divider />
          <SaveButton
            disabled={savingKey === 'features'}
            onClick={() =>
              handleSave('features', async () => {
                if (!featureFlags) return false;
                const result = await appConfigService.updateFeatureFlags(featureFlags);
                return result.success;
              })
            }
          >
            Save Feature Flags
          </SaveButton>
        </Card>
      )}

      {activeTab === 'notifications' && notificationSettings && (
        <Card>
          <CardTitle>Notification Settings</CardTitle>
          <CardSubtitle>Defaults for new users and quiet hours.</CardSubtitle>
          <Section>
            <Card>
              <CardTitle style={{ fontSize: '1rem' }}>Default Categories</CardTitle>
              <Section>
                {Object.entries(notificationSettings.default_categories).map(([key, enabled]) => (
                  <FieldRow key={key}>
                    <Label>{key}</Label>
                    <Toggle
                      type="checkbox"
                      checked={enabled}
                      onChange={(event) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          default_categories: {
                            ...notificationSettings.default_categories,
                            [key]: event.target.checked,
                          },
                        })
                      }
                    />
                  </FieldRow>
                ))}
              </Section>
            </Card>
            <Card>
              <CardTitle style={{ fontSize: '1rem' }}>Quiet Hours</CardTitle>
              <Section>
                <FieldRow>
                  <Label>Enabled</Label>
                  <Toggle
                    type="checkbox"
                    checked={notificationSettings.quiet_hours.enabled}
                    onChange={(event) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        quiet_hours: {
                          ...notificationSettings.quiet_hours,
                          enabled: event.target.checked,
                        },
                      })
                    }
                  />
                </FieldRow>
                <FieldRow>
                  <Label>Start Time</Label>
                  <TextInput
                    value={notificationSettings.quiet_hours.start_time}
                    onChange={(event) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        quiet_hours: {
                          ...notificationSettings.quiet_hours,
                          start_time: event.target.value,
                        },
                      })
                    }
                  />
                </FieldRow>
                <FieldRow>
                  <Label>End Time</Label>
                  <TextInput
                    value={notificationSettings.quiet_hours.end_time}
                    onChange={(event) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        quiet_hours: {
                          ...notificationSettings.quiet_hours,
                          end_time: event.target.value,
                        },
                      })
                    }
                  />
                </FieldRow>
              </Section>
            </Card>
            <FieldRow>
              <Label>Batch Notifications</Label>
              <Toggle
                type="checkbox"
                checked={notificationSettings.batch_notifications}
                onChange={(event) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    batch_notifications: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Notification Sound</Label>
              <Toggle
                type="checkbox"
                checked={notificationSettings.notification_sound_enabled}
                onChange={(event) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    notification_sound_enabled: event.target.checked,
                  })
                }
              />
            </FieldRow>
            <FieldRow>
              <Label>Vibration</Label>
              <Toggle
                type="checkbox"
                checked={notificationSettings.vibration_enabled}
                onChange={(event) =>
                  setNotificationSettings({
                    ...notificationSettings,
                    vibration_enabled: event.target.checked,
                  })
                }
              />
            </FieldRow>
          </Section>
          <Divider />
          <SaveButton
            disabled={savingKey === 'notifications'}
            onClick={() =>
              handleSave('notifications', async () => {
                if (!notificationSettings) return false;
                const result = await appConfigService.updateNotificationSettings(notificationSettings);
                return result.success;
              })
            }
          >
            Save Notification Settings
          </SaveButton>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card>
          <CardTitle>Push Notification Roles</CardTitle>
          <CardSubtitle>Set which roles receive each push notification type.</CardSubtitle>
          <Section>
            <LogTableWrap>
              <LogTable>
                <thead>
                  <tr>
                    <th>Push Notification Type</th>
                    <th>Roles</th>
                  </tr>
                </thead>
                <tbody>
                  {PUSH_NOTIFICATION_ROLE_ROWS.map((row) => {
                    const currentRoles =
                      pushNotificationRoleConfig?.[row.key] ??
                      DEFAULT_PUSH_NOTIFICATION_ROLE_CONFIG[row.key] ??
                      [];
                    return (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>
                          <RoleOptionsGrid>
                            {ROLE_OPTIONS.map((role) => (
                              <RoleOption key={`${row.key}-${role.value}`}>
                                <Toggle
                                  type="checkbox"
                                  checked={currentRoles.includes(role.value)}
                                  onChange={() => handleTogglePushRole(row.key, role.value)}
                                />
                                {role.label}
                              </RoleOption>
                            ))}
                          </RoleOptionsGrid>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </LogTable>
            </LogTableWrap>
            <Divider />
            <SaveButton disabled={!pushNotificationRolesDirty || pushNotificationRolesSaving} onClick={handleSavePushRoles}>
              {pushNotificationRolesSaving ? 'Saving...' : 'Save Push Notification Roles'}
            </SaveButton>
          </Section>
        </Card>
      )}

      {activeTab === 'edit-log' && (
        <Card>
          <CardTitle>User Edit Log</CardTitle>
          <CardSubtitle>Recent updates to user profiles and roles.</CardSubtitle>
          <Section>
            <InlineRow>
              <SaveButton disabled={editLogsLoading} onClick={loadEditLogs}>
                {editLogsLoading ? 'Refreshing...' : 'Refresh Log'}
              </SaveButton>
              <SaveButton disabled={filteredEditLogs.length === 0} onClick={handleExportEditLogs}>
                Export CSV
              </SaveButton>
              <Select value={editLogSourceFilter} onChange={(event) => setEditLogSourceFilter(event.target.value)}>
                <option value="all">All types</option>
                {editLogSources.map((source) => (
                  <option key={source} value={source}>
                    {source.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
              <TextInput
                type="search"
                placeholder="Search user, actor, or changes"
                value={editLogSearch}
                onChange={(event) => setEditLogSearch(event.target.value)}
              />
              <TextInput
                type="date"
                value={editLogFromDate}
                onChange={(event) => setEditLogFromDate(event.target.value)}
              />
              <TextInput
                type="date"
                value={editLogToDate}
                onChange={(event) => setEditLogToDate(event.target.value)}
              />
              <Label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                <Toggle
                  type="checkbox"
                  checked={groupEditEvents}
                  onChange={(event) => setGroupEditEvents(event.target.checked)}
                />
                Group events
              </Label>
              {editLogsLoading && <SmallText>Loading edits</SmallText>}
            </InlineRow>
            {editLogsError && <Message $type="error">{editLogsError}</Message>}
            {!editLogsLoading && groupedEditLogs.length === 0 ? (
              <EmptyState>No edits logged yet.</EmptyState>
            ) : (
              <LogTableWrap>
                <LogTable>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>Edited By</th>
                      <th>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedEditLogs.map((log) => {
                      const targetLabel =
                        log.user?.full_name || log.user?.email || log.user?.id || 'Unknown user';
                      const actorLabel =
                        log.actor_name ||
                        log.actor_email ||
                        log.actor?.full_name ||
                        log.actor?.email ||
                        log.actor?.id ||
                        'System';
                      const actorRoles = log.actor_roles?.length
                        ? log.actor_roles.map((role) => getRoleLabel(role)).join(', ')
                        : null;
                      const changeEntries = Object.entries(log.changes ?? {}).filter(
                        ([field]) => !shouldHideChangeField(field),
                      );
                      const eventCount = (log as UserEditLog & { event_count?: number }).event_count ?? 1;
                      return (
                        <tr key={log.id}>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                          <td>
                            {formatSourceLabel(log.source_table, log.action)}
                            {eventCount > 1 ? ` (${eventCount} events)` : ''}
                          </td>
                          <td>{targetLabel}</td>
                          <td>
                            {actorLabel}
                            {actorRoles ? <SmallText>Roles: {actorRoles}</SmallText> : null}
                          </td>
                          <td>
                            {changeEntries.length ? (
                              <ChangeList>
                                {changeEntries.map(([field, values]) => (
                                  <ChangeItem key={field}>
                                    {field}: {formatChangeDiff(field, values?.old, values?.new, log, targetLabel, actorLabel)}
                                  </ChangeItem>
                                ))}
                              </ChangeList>
                            ) : (
                              ''
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </LogTable>
              </LogTableWrap>
            )}
          </Section>
        </Card>
      )}

      {activeTab === 'analytics' && (
        <Card>
          <CardTitle>Analytics</CardTitle>
          <CardSubtitle>
            Aggregated metrics from Supabase analytics views. Each card is one view or dataset; refresh loads all
            sections.
          </CardSubtitle>
          <PerformanceTabStack>
            <PerfToolbar>
              <PerfToolbarCluster>
                <PerfPrimaryButton disabled={analyticsLoading} onClick={loadAnalytics}>
                  {analyticsLoading ? 'Refreshing' : 'Refresh'}
                </PerfPrimaryButton>
              </PerfToolbarCluster>
              {analyticsLoading ? <PerfToolbarMeta>Loading analytics</PerfToolbarMeta> : null}
            </PerfToolbar>
            {analyticsError && <Message $type="error">{analyticsError}</Message>}
            {analyticsSections.length === 0 && !analyticsLoading ? (
              <EmptyState>
                No analytics data yet. Ensure the analytics views are created and accessible.
              </EmptyState>
            ) : (
              <AnalyticsSectionsGrid>
                {analyticsSections.map((section) => (
                  <PerfSubCard key={section.id}>
                    <PerfSubCardHeader>
                      <PerfSubCardTitle>{section.label}</PerfSubCardTitle>
                      {section.description ? <PerfSubCardDesc>{section.description}</PerfSubCardDesc> : null}
                    </PerfSubCardHeader>
                    {section.error ? (
                      <Message $type="error">{section.error}</Message>
                    ) : (
                      renderAnalyticsMetrics(section.data)
                    )}
                  </PerfSubCard>
                ))}
              </AnalyticsSectionsGrid>
            )}
          </PerformanceTabStack>
        </Card>
      )}

      {activeTab === 'monitoring' && <SuperAdminMonitoringTab />}

      {activeTab === 'security' && <SuperAdminSecurityTab />}

      {activeTab === 'performance' && (
        <Card>
          <CardTitle>Performance</CardTitle>
          <CardSubtitle>
            Mobile telemetry snapshots, crash detection, client errors/warnings, and deduplicated Cursor fix prompts.
          </CardSubtitle>
          <Tabs style={{ marginBottom: '1rem' }}>
            <TabButton
              type="button"
              $active={performanceSubTab === 'logs'}
              onClick={() => setPerformanceSubTab('logs')}
            >
              Logs
            </TabButton>
            <TabButton
              type="button"
              $active={performanceSubTab === 'crashes'}
              onClick={() => setPerformanceSubTab('crashes')}
            >
              Crashes
            </TabButton>
            <TabButton
              type="button"
              $active={performanceSubTab === 'fixes'}
              onClick={() => setPerformanceSubTab('fixes')}
            >
              Fix prompts
            </TabButton>
          </Tabs>
          {performanceSubTab === 'fixes' ? (
            <SuperAdminPerformanceSolutionsTab />
          ) : performanceSubTab === 'crashes' ? (
            <SuperAdminCrashesTab />
          ) : (
          <PerformanceTabStack>
            <PerfSubCard>
              <PerfSubCardHeader>
                <PerfSubCardTitle>Mobile snapshots</PerfSubCardTitle>
                <PerfSubCardDesc>
                  Device-reported optimizer load, upload queue depth, and realtime connection status.
                </PerfSubCardDesc>
              </PerfSubCardHeader>
              <PerfToolbar>
                <PerfToolbarCluster>
                  <PerfPrimaryButton disabled={performanceLoading} onClick={loadPerformanceSnapshots}>
                    {performanceLoading ? 'Refreshing' : 'Refresh'}
                  </PerfPrimaryButton>
                  <PerfDangerButton disabled={performanceLoading} onClick={handleClearPerformanceSnapshots}>
                    Clear all snapshots
                  </PerfDangerButton>
                </PerfToolbarCluster>
                <PerfToolbarSpacer />
                <PerfToolbarCluster>
                  <PerfToolbarMeta>Rows per page</PerfToolbarMeta>
                  <PerfSelect
                    value={String(performancePageSize)}
                    aria-label="Snapshots page size"
                    onChange={(event) => {
                      setPerformancePage(0);
                      setPerformancePageSize(Number(event.target.value));
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </PerfSelect>
                </PerfToolbarCluster>
              </PerfToolbar>
              {performanceFeedbackToShow && (
                <PerfFeedback $tone={performanceFeedbackToShow.tone}>
                  {performanceFeedbackToShow.text}
                </PerfFeedback>
              )}
              {performanceSnapshots.length === 0 && !performanceLoading ? (
                <EmptyState>No performance snapshots recorded yet.</EmptyState>
              ) : (
                <>
                  {performanceStats && (
                    <PerformanceGrid>
                      <PerfStatTile>
                        <MetricLabel>
                          Avg Active Ops
                          <InfoIcon title="Average active operations from the current page of snapshots.">?</InfoIcon>
                        </MetricLabel>
                        <MetricValue $bad={performanceStats.avgActive >= 3}>
                          {performanceStats.avgActive.toFixed(1)}
                        </MetricValue>
                        <ChartWrap>{renderSparkline(performanceStats.activeSeries, 'var(--info)')}</ChartWrap>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>
                          Avg Pending Ops
                          <InfoIcon title="Average queued operations from the current page of snapshots.">?</InfoIcon>
                        </MetricLabel>
                        <MetricValue $bad={performanceStats.avgPending >= 5}>
                          {performanceStats.avgPending.toFixed(1)}
                        </MetricValue>
                        <ChartWrap>{renderSparkline(performanceStats.pendingSeries, 'var(--warning)')}</ChartWrap>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>
                          Avg Upload Queue
                          <InfoIcon title="Average queued uploads from the current page of snapshots.">?</InfoIcon>
                        </MetricLabel>
                        <MetricValue $bad={performanceStats.avgQueue >= 3}>
                          {performanceStats.avgQueue.toFixed(1)}
                        </MetricValue>
                        <ChartWrap>{renderSparkline(performanceStats.queueSeries, 'var(--success)')}</ChartWrap>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>
                          Realtime Connected %
                          <InfoIcon title="Share of snapshots on this page reporting an active realtime connection.">?</InfoIcon>
                        </MetricLabel>
                        <MetricValue $bad={performanceStats.connectedRate < 0.9}>
                          {(performanceStats.connectedRate * 100).toFixed(0)}%
                        </MetricValue>
                        <ChartWrap>{renderSparkline(performanceStats.channelsSeries, 'var(--accent)')}</ChartWrap>
                      </PerfStatTile>
                    </PerformanceGrid>
                  )}
                  {monitoringCoverageStats && (
                    <PerformanceGrid>
                      <PerfStatTile>
                        <MetricLabel>Snapshots (24h)</MetricLabel>
                        <MetricValue>{monitoringCoverageStats.snapshots24h}</MetricValue>
                        <SmallText>
                          Last seen:{' '}
                          {monitoringCoverageStats.lastSeen.snapshots
                            ? new Date(monitoringCoverageStats.lastSeen.snapshots).toLocaleString()
                            : 'a'}
                        </SmallText>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Errors/Warnings (24h)</MetricLabel>
                        <MetricValue $bad={monitoringCoverageStats.errors24h > 0}>
                          {monitoringCoverageStats.errors24h}
                        </MetricValue>
                        <SmallText>
                          Last seen:{' '}
                          {monitoringCoverageStats.lastSeen.errors
                            ? new Date(monitoringCoverageStats.lastSeen.errors).toLocaleString()
                            : 'a'}
                        </SmallText>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Telemetry (24h)</MetricLabel>
                        <MetricValue>{monitoringCoverageStats.telemetry24h}</MetricValue>
                        <SmallText>
                          Last seen:{' '}
                          {monitoringCoverageStats.lastSeen.telemetry
                            ? new Date(monitoringCoverageStats.lastSeen.telemetry).toLocaleString()
                            : 'a'}
                        </SmallText>
                      </PerfStatTile>
                    </PerformanceGrid>
                  )}
                  <PerfTableShell aria-busy={performanceLoading}>
                    <PerfTableScroll>
                      <PerfTable>
                        <thead>
                          <tr>
                            <th>{renderMetricHeader('When', 'Time the snapshot was recorded.')}</th>
                            <th>{renderMetricHeader('User', 'User that sent the snapshot.')}</th>
                            <th>{renderMetricHeader('Platform', 'Device OS for the snapshot.')}</th>
                            <th>{renderMetricHeader('Version', 'App version reported by the device.')}</th>
                            <th>{renderMetricHeader('Active Ops', 'Operations currently running in PerformanceOptimizer.')}</th>
                            <th>{renderMetricHeader('Pending Ops', 'Operations queued in PerformanceOptimizer.')}</th>
                            <th>{renderMetricHeader('Max Ops', 'Max concurrent operations configured in PerformanceOptimizer.')}</th>
                            <th>{renderMetricHeader('Realtime', 'Current realtime connection status & channel count.')}</th>
                            <th>{renderMetricHeader('Upload Queue', 'Number of pending upload tasks on device.')}</th>
                            <th>{renderMetricHeader('Background', 'Whether the app was in background when recorded.')}</th>
                            <th>{renderMetricHeader('Source', 'Reason the snapshot was recorded.')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {performanceSnapshots.map((snapshot) => {
                            const isPendingBad = (snapshot.pending_operations ?? 0) >= 5;
                            const isQueueBad = (snapshot.upload_queue_depth ?? 0) >= 3;
                            const isRealtimeBad = snapshot.ws_connected === false;
                            const isActiveBad =
                              snapshot.max_concurrent_operations !== null &&
                              snapshot.max_concurrent_operations !== undefined &&
                              (snapshot.active_operations ?? 0) >= snapshot.max_concurrent_operations;
                            return (
                              <tr key={snapshot.id}>
                                <td>{new Date(snapshot.created_at).toLocaleString()}</td>
                                <td>{getPerformanceUserLabel(snapshot.user_id)}</td>
                                <td>{snapshot.platform ?? ''}</td>
                                <td>{snapshot.app_version ?? ''}</td>
                                <td style={{ color: isActiveBad ? 'var(--error)' : undefined }}>
                                  {snapshot.active_operations ?? ''}
                                </td>
                                <td style={{ color: isPendingBad ? 'var(--error)' : undefined }}>
                                  {snapshot.pending_operations ?? ''}
                                </td>
                                <td>{snapshot.max_concurrent_operations ?? ''}</td>
                                <td>
                                  <span style={{ color: isRealtimeBad ? 'var(--error)' : undefined }}>
                                    {snapshot.ws_connected === null || snapshot.ws_connected === undefined
                                      ? ''
                                      : snapshot.ws_connected
                                        ? `Connected (${snapshot.realtime_channels ?? 0})`
                                        : (
                                          <LinkButton onClick={() => setSelectedRealtimeSnapshot(snapshot)}>
                                            Disconnected ({snapshot.realtime_channels ?? 0})
                                          </LinkButton>
                                        )}
                                  </span>
                                </td>
                                <td style={{ color: isQueueBad ? 'var(--error)' : undefined }}>
                                  {snapshot.upload_queue_depth ?? ''}
                                </td>
                                <td>{snapshot.is_in_background ? 'Yes' : 'No'}</td>
                                <td>{snapshot.source ?? ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </PerfTable>
                    </PerfTableScroll>
                    <PerformanceTablePager
                      loading={performanceLoading}
                      page={performancePage}
                      pageSize={performancePageSize}
                      totalCount={performanceTotalCount}
                      rowCount={performanceSnapshots.length}
                      onPageChange={setPerformancePage}
                    />
                  </PerfTableShell>
                </>
              )}
            </PerfSubCard>

            <PerfSubCard>
              <PerfSubCardHeader>
                <PerfSubCardTitle>Telemetry</PerfSubCardTitle>
                <PerfSubCardDesc>
                  High-signal diagnostics and info telemetry events with server-side paging and filters.
                </PerfSubCardDesc>
              </PerfSubCardHeader>
              <PerfToolbar>
                <PerfToolbarCluster>
                  <PerfPrimaryButton disabled={telemetryLoading} onClick={loadTelemetryEvents}>
                    {telemetryLoading ? 'Refreshing...' : 'Refresh'}
                  </PerfPrimaryButton>
                </PerfToolbarCluster>
                <PerfToolbarCluster>
                  <PerfSelect
                    value={telemetryLevelFilter}
                    aria-label="Filter telemetry by level"
                    onChange={(event) => {
                      setTelemetryPage(0);
                      setTelemetryLevelFilter(event.target.value as TelemetryLevelFilter);
                    }}
                  >
                    <option value="all">All levels</option>
                    <option value="error">Errors</option>
                    <option value="warning">Warnings</option>
                    <option value="info">Info</option>
                  </PerfSelect>
                  <PerfSelect
                    value={telemetrySourceFilter}
                    aria-label="Filter telemetry by source"
                    onChange={(event) => {
                      setTelemetryPage(0);
                      setTelemetrySourceFilter(event.target.value);
                    }}
                  >
                    <option value="all">All sources</option>
                    {telemetrySourceOptions.map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </PerfSelect>
                  <PerfSelect
                    value={telemetryEventFilter}
                    aria-label="Filter telemetry by event"
                    onChange={(event) => {
                      setTelemetryPage(0);
                      setTelemetryEventFilter(event.target.value);
                    }}
                  >
                    <option value="all">All events</option>
                    {telemetryEventOptions.map((eventName) => (
                      <option key={eventName} value={eventName}>
                        {eventName}
                      </option>
                    ))}
                  </PerfSelect>
                  <PerfSearchInput
                    placeholder="Search telemetry..."
                    value={telemetrySearchInput}
                    onChange={(event) => setTelemetrySearchInput(event.target.value)}
                    aria-label="Search telemetry"
                  />
                  <PerfPrimaryButton
                    disabled={telemetryLoading}
                    onClick={() => {
                      setTelemetryPage(0);
                      setTelemetrySearch(telemetrySearchInput.trim());
                    }}
                  >
                    Search
                  </PerfPrimaryButton>
                </PerfToolbarCluster>
                <PerfToolbarSpacer />
                <PerfToolbarCluster>
                  <PerfToolbarMeta>Rows per page</PerfToolbarMeta>
                  <PerfSelect
                    value={String(telemetryPageSize)}
                    aria-label="Telemetry page size"
                    onChange={(event) => {
                      setTelemetryPage(0);
                      setTelemetryPageSize(Number(event.target.value));
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </PerfSelect>
                </PerfToolbarCluster>
              </PerfToolbar>
              {telemetryFeedbackToShow && (
                <PerfFeedback $tone={telemetryFeedbackToShow.tone}>
                  {telemetryFeedbackToShow.text}
                </PerfFeedback>
              )}
              {telemetryStats && (
                <PerformanceGrid>
                  <PerfStatTile>
                    <MetricLabel>Total (this page)</MetricLabel>
                    <MetricValue>{telemetryStats.total}</MetricValue>
                  </PerfStatTile>
                  <PerfStatTile>
                    <MetricLabel>Errors</MetricLabel>
                    <MetricValue $bad={telemetryStats.errors > 0}>{telemetryStats.errors}</MetricValue>
                  </PerfStatTile>
                  <PerfStatTile>
                    <MetricLabel>Warnings</MetricLabel>
                    <MetricValue $bad={telemetryStats.warnings > 0}>{telemetryStats.warnings}</MetricValue>
                  </PerfStatTile>
                  <PerfStatTile>
                    <MetricLabel>Info</MetricLabel>
                    <MetricValue>{telemetryStats.infos}</MetricValue>
                  </PerfStatTile>
                </PerformanceGrid>
              )}
              <Section>
                <PerfSubCardHeader>
                  <PerfSubCardTitle>Auth refresh failure paths</PerfSubCardTitle>
                  <PerfSubCardDesc>
                    Breakdown of recent mobile `auth-session-refresh` failures (fatal/error/suppressed).
                    `rotation_race` is recoverable token reuse (no logout). `invalid_session` is a true rejected refresh token.
                    `transient:secure-store-locked` is an iOS Keychain write while locked/background (no logout).
                    In-flight SDK `SIGNED_OUT` during `refreshSession` is excluded unless classified fatal.
                  </PerfSubCardDesc>
                </PerfSubCardHeader>
                {sessionLossSummaryError ? <SmallText>{sessionLossSummaryError}</SmallText> : null}
                {sessionLossSummaryLoading ? <SmallText>Loading auth refresh failure breakdown...</SmallText> : null}
                {!sessionLossSummaryLoading && !sessionLossSummaryError && sessionLossSummary.length === 0 ? (
                  <SmallText>No auth refresh failure events found in the recent telemetry sample.</SmallText>
                ) : null}
                {!sessionLossSummaryLoading && sessionLossSummary.length > 0 ? (
                  <>
                    <PerformanceGrid>
                      <PerfStatTile>
                        <MetricLabel>Total failure events</MetricLabel>
                        <MetricValue $bad={sessionLossSummaryTotal > 0}>{sessionLossSummaryTotal}</MetricValue>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Top failing path</MetricLabel>
                        <MetricValue $bad>
                          {sessionLossSummary[0]?.reason ?? 'n/a'}
                        </MetricValue>
                      </PerfStatTile>
                    </PerformanceGrid>
                    <PerfTableShell>
                      <PerfTableScroll>
                        <PerfTable>
                          <thead>
                            <tr>
                              <th>Failing path</th>
                              <th>Class</th>
                              <th>Logout</th>
                              <th>Count</th>
                              <th>Last seen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionLossSummary.slice(0, 8).map((entry) => (
                              <tr key={entry.reason}>
                                <td>{entry.reason}</td>
                                <td>{entry.severity ?? entry.failureClass ?? 'n/a'}</td>
                                <td>{entry.logoutEnforced ? 'Yes' : 'No'}</td>
                                <td>{entry.count}</td>
                                <td>{new Date(entry.last_seen_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </PerfTable>
                      </PerfTableScroll>
                    </PerfTableShell>
                  </>
                ) : null}
              </Section>
              {telemetryEvents.length === 0 && !telemetryLoading ? (
                <EmptyState>No telemetry events recorded yet.</EmptyState>
              ) : (
                <PerfTableShell aria-busy={telemetryLoading}>
                  <PerfTableScroll>
                    <PerfTable>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Level</th>
                          <th>Event</th>
                          <th>User</th>
                          <th>Source</th>
                          <th>Session</th>
                          <th>Platform</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {telemetryEvents.map((event) => (
                          <tr key={event.id}>
                            <td>{new Date(event.created_at).toLocaleString()}</td>
                            <td>
                              <LevelBadge $level={event.level as AppErrorLevel}>{event.level}</LevelBadge>
                            </td>
                            <td>{event.event_name}</td>
                            <td>{getTelemetryUserLabel(event.user_id)}</td>
                            <td>{event.source ?? 'a'}</td>
                            <td>{event.session_id ? compactId(event.session_id) : 'a'}</td>
                            <td>{event.platform ?? 'a'}</td>
                            <td>
                              <DetailButton onClick={() => setSelectedTelemetryEvent(event)}>
                                {truncateText(event.message || event.event_name, 120)}
                              </DetailButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </PerfTable>
                  </PerfTableScroll>
                  <PerformanceTablePager
                    loading={telemetryLoading}
                    page={telemetryPage}
                    pageSize={telemetryPageSize}
                    totalCount={telemetryTotalCount}
                    rowCount={telemetryEvents.length}
                    onPageChange={setTelemetryPage}
                  />
                </PerfTableShell>
              )}
            </PerfSubCard>

            <PerfSubCard>
              <PerfSubCardHeader>
                <PerfSubCardTitle>Monitoring alerts</PerfSubCardTitle>
                <PerfSubCardDesc>
                  Anomaly detector outputs with acknowledgement and resolution actions.
                </PerfSubCardDesc>
              </PerfSubCardHeader>
              <PerfToolbar>
                <PerfToolbarCluster>
                  <PerfPrimaryButton disabled={monitoringAlertsLoading} onClick={loadMonitoringAlerts}>
                    {monitoringAlertsLoading ? 'Refreshing...' : 'Refresh'}
                  </PerfPrimaryButton>
                </PerfToolbarCluster>
                <PerfToolbarCluster>
                  <PerfSelect
                    value={monitoringAlertStatusFilter}
                    aria-label="Filter alerts by status"
                    onChange={(event) => {
                      setMonitoringAlertsPage(0);
                      setMonitoringAlertStatusFilter(event.target.value as AlertStatusFilter);
                    }}
                  >
                    <option value="all">All statuses</option>
                    <option value="open">Open</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="resolved">Resolved</option>
                  </PerfSelect>
                  <PerfSelect
                    value={monitoringAlertSeverityFilter}
                    aria-label="Filter alerts by severity"
                    onChange={(event) => {
                      setMonitoringAlertsPage(0);
                      setMonitoringAlertSeverityFilter(event.target.value as AlertSeverityFilter);
                    }}
                  >
                    <option value="all">All severities</option>
                    <option value="critical">Critical</option>
                    <option value="error">Error</option>
                    <option value="warning">Warning</option>
                    <option value="info">Info</option>
                  </PerfSelect>
                </PerfToolbarCluster>
                <PerfToolbarSpacer />
                <PerfToolbarCluster>
                  <PerfToolbarMeta>Rows per page</PerfToolbarMeta>
                  <PerfSelect
                    value={String(monitoringAlertsPageSize)}
                    aria-label="Monitoring alerts page size"
                    onChange={(event) => {
                      setMonitoringAlertsPage(0);
                      setMonitoringAlertsPageSize(Number(event.target.value));
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </PerfSelect>
                </PerfToolbarCluster>
              </PerfToolbar>
              {monitoringAlertsFeedback && (
                <PerfFeedback $tone={monitoringAlertsFeedback.tone}>
                  {monitoringAlertsFeedback.text}
                </PerfFeedback>
              )}
              {monitoringAlertsError && <SmallText>{monitoringAlertsError}</SmallText>}
              {monitoringAlerts.length === 0 && !monitoringAlertsLoading ? (
                <EmptyState>No alerts recorded yet.</EmptyState>
              ) : (
                <PerfTableShell aria-busy={monitoringAlertsLoading}>
                  <PerfTableScroll>
                    <PerfTable>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Status</th>
                          <th>Severity</th>
                          <th>Source</th>
                          <th>Title</th>
                          <th>Occurrences</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monitoringAlerts.map((alert) => (
                          <tr key={alert.id}>
                            <td>{new Date(alert.created_at).toLocaleString()}</td>
                            <td>{alert.status}</td>
                            <td>{alert.severity}</td>
                            <td>{alert.source}</td>
                            <td>{alert.title}</td>
                            <td>{alert.occurrence_count}</td>
                            <td>
                              {alert.status === 'open' && (
                                <InlineRow>
                                  <PerfGhostButton type="button" onClick={() => void handleAcknowledgeAlert(alert.id)}>
                                    Acknowledge
                                  </PerfGhostButton>
                                  <PerfPrimaryButton type="button" onClick={() => void handleResolveAlert(alert.id)}>
                                    Resolve
                                  </PerfPrimaryButton>
                                </InlineRow>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </PerfTable>
                  </PerfTableScroll>
                  <PerformanceTablePager
                    loading={monitoringAlertsLoading}
                    page={monitoringAlertsPage}
                    pageSize={monitoringAlertsPageSize}
                    totalCount={monitoringAlertsTotalCount}
                    rowCount={monitoringAlerts.length}
                    onPageChange={setMonitoringAlertsPage}
                  />
                </PerfTableShell>
              )}
              {monitoringAlertRuns.length > 0 && (
                <Section>
                  <SmallText>Recent detector runs</SmallText>
                  <PerfTableShell>
                    <PerfTableScroll>
                      <PerfTable>
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Detector</th>
                            <th>Outcome</th>
                            <th>Created</th>
                            <th>Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monitoringAlertRuns.slice(0, 8).map((run) => (
                            <tr key={run.id}>
                              <td>{new Date(run.created_at).toLocaleString()}</td>
                              <td>{run.detector_name}</td>
                              <td>{run.outcome}</td>
                              <td>{run.alerts_created}</td>
                              <td>{run.alerts_updated}</td>
                            </tr>
                          ))}
                        </tbody>
                      </PerfTable>
                    </PerfTableScroll>
                  </PerfTableShell>
                </Section>
              )}
            </PerfSubCard>

            <PerfSubCard>
              <PerfSubCardHeader>
                <PerfSubCardTitle>App error monitor</PerfSubCardTitle>
                <PerfSubCardDesc>
                  Client-side errors and warnings captured from the web and mobile apps (paged; filters apply server-side). Fatal crashes are grouped on the Crashes tab.
                </PerfSubCardDesc>
              </PerfSubCardHeader>
              <PerfToolbar>
                <PerfToolbarCluster>
                  <PerfPrimaryButton disabled={appErrorLoading} onClick={loadAppErrorEvents}>
                    {appErrorLoading ? 'Refreshing' : 'Refresh'}
                  </PerfPrimaryButton>
                  <PerfDangerButton disabled={appErrorLoading} onClick={handleClearAppErrorEvents}>
                    Clear all events
                  </PerfDangerButton>
                </PerfToolbarCluster>
                <PerfToolbarCluster>
                  <PerfSelect
                    value={appErrorLevelFilter}
                    aria-label="Filter by severity"
                    onChange={(event) => {
                      setAppErrorPage(0);
                      setAppErrorLevelFilter(event.target.value as AppErrorLevelFilter);
                    }}
                  >
                    <option value="all">All levels</option>
                    <option value="error">Errors</option>
                    <option value="warning">Warnings</option>
                    <option value="info">Info</option>
                  </PerfSelect>
                  <PerfSearchInput
                    placeholder="Search message"
                    value={appErrorSearchInput}
                    onChange={(event) => setAppErrorSearchInput(event.target.value)}
                    aria-label="Search error messages"
                  />
                  <PerfPrimaryButton
                    disabled={appErrorLoading}
                    onClick={() => {
                      setAppErrorPage(0);
                      setAppErrorSearch(appErrorSearchInput.trim());
                    }}
                  >
                    Search
                  </PerfPrimaryButton>
                  <PerfGhostButton
                    type="button"
                    disabled={appErrorLoading || (!appErrorSearch && !appErrorSearchInput)}
                    onClick={() => {
                      setAppErrorPage(0);
                      setAppErrorSearch('');
                      setAppErrorSearchInput('');
                    }}
                  >
                    Clear search
                  </PerfGhostButton>
                </PerfToolbarCluster>
                <PerfToolbarSpacer />
                <PerfToolbarCluster>
                  <PerfToolbarMeta>Rows per page</PerfToolbarMeta>
                  <PerfSelect
                    value={String(appErrorPageSize)}
                    aria-label="Error events page size"
                    onChange={(event) => {
                      setAppErrorPage(0);
                      setAppErrorPageSize(Number(event.target.value));
                    }}
                  >
                    {[25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </PerfSelect>
                  {appErrorLoading && <PerfToolbarMeta>Updating</PerfToolbarMeta>}
                </PerfToolbarCluster>
              </PerfToolbar>
              {appErrorFeedbackToShow && (
                <PerfFeedback $tone={appErrorFeedbackToShow.tone}>
                  {appErrorFeedbackToShow.text}
                </PerfFeedback>
              )}
              {appErrorEvents.length === 0 && !appErrorLoading ? (
                <EmptyState>No error events recorded yet.</EmptyState>
              ) : (
                <>
                  {appErrorStats && (
                    <PerformanceGrid>
                      <PerfStatTile>
                        <MetricLabel>Total (this page)</MetricLabel>
                        <MetricValue>{appErrorStats.total}</MetricValue>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Errors</MetricLabel>
                        <MetricValue $bad={appErrorStats.errors > 0}>{appErrorStats.errors}</MetricValue>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Warnings</MetricLabel>
                        <MetricValue $bad={appErrorStats.warnings > 0}>{appErrorStats.warnings}</MetricValue>
                      </PerfStatTile>
                      <PerfStatTile>
                        <MetricLabel>Info</MetricLabel>
                        <MetricValue>{appErrorStats.infos}</MetricValue>
                      </PerfStatTile>
                    </PerformanceGrid>
                  )}
                  <PerfTableShell aria-busy={appErrorLoading}>
                    <PerfTableScroll>
                      <PerfTable>
                        <thead>
                          <tr>
                            <th>{renderMetricHeader('When', 'Time the error event was captured.')}</th>
                            <th>{renderMetricHeader('Level', 'Severity reported by the client.')}</th>
                            <th>{renderMetricHeader('Message', 'Short error message or warning text.')}</th>
                            <th>{renderMetricHeader('User', 'User ID that reported the error (if signed in).')}</th>
                            <th>{renderMetricHeader('Source', 'Where the error was captured.')}</th>
                            <th>{renderMetricHeader('Platform', 'Client platform reporting the error.')}</th>
                            <th>{renderMetricHeader('Version', 'App version, if reported.')}</th>
                            <th>{renderMetricHeader('URL', 'Page URL where it occurred.')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appErrorEvents.map((event) => (
                            <tr key={event.id}>
                              <td>{new Date(event.created_at).toLocaleString()}</td>
                              <td>
                                <LevelBadge $level={event.level}>{event.level}</LevelBadge>
                              </td>
                              <td>
                                <DetailButton onClick={() => setSelectedAppErrorEvent(event)}>
                                  {truncateText(event.message, 120)}
                                </DetailButton>
                              </td>
                              <td>{getAppErrorUserLabel(event.user_id)}</td>
                              <td>{event.source ?? ''}</td>
                              <td>{event.platform ?? ''}</td>
                              <td>{event.app_version ?? ''}</td>
                              <td>
                                {event.url ? (
                                  <ExternalLink href={event.url} target="_blank" rel="noreferrer">
                                    Open
                                  </ExternalLink>
                                ) : (
                                  ''
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </PerfTable>
                    </PerfTableScroll>
                    <PerformanceTablePager
                      loading={appErrorLoading}
                      page={appErrorPage}
                      pageSize={appErrorPageSize}
                      totalCount={appErrorTotalCount}
                      rowCount={appErrorEvents.length}
                      onPageChange={setAppErrorPage}
                    />
                  </PerfTableShell>
                </>
              )}
            </PerfSubCard>
          </PerformanceTabStack>
          )}
        </Card>
      )}

      {selectedRealtimeSnapshot && (
        <ModalOverlay onClick={() => setSelectedRealtimeSnapshot(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <HeaderRow>
              <div>
                <CardTitle>Realtime Disconnected</CardTitle>
                <CardSubtitle>Snapshot details for troubleshooting.</CardSubtitle>
              </div>
              <SaveButton type="button" onClick={() => setSelectedRealtimeSnapshot(null)}>
                Close
              </SaveButton>
            </HeaderRow>
            <Divider />
            <Section>
              <FieldRow>
                <Label>Recorded At</Label>
                <SmallText>{new Date(selectedRealtimeSnapshot.created_at).toLocaleString()}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>User</Label>
                <SmallText>{selectedRealtimeSnapshot.user_id ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Platform</Label>
                <SmallText>{selectedRealtimeSnapshot.platform ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>App Version</Label>
                <SmallText>{selectedRealtimeSnapshot.app_version ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Session ID</Label>
                <SmallText>{selectedRealtimeSnapshot.session_id ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Device</Label>
                <SmallText>
                  {selectedRealtimeSnapshot.metadata?.device?.manufacturer ?? ''}{' '}
                  {selectedRealtimeSnapshot.metadata?.device?.model ?? ''}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>OS</Label>
                <SmallText>
                  {selectedRealtimeSnapshot.metadata?.device?.osName ?? ''}{' '}
                  {selectedRealtimeSnapshot.metadata?.device?.osVersion ?? ''}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Network</Label>
                <SmallText>
                  {selectedRealtimeSnapshot.metadata?.network?.type ?? ''} /{' '}
                  {selectedRealtimeSnapshot.metadata?.network?.quality ?? ''} / connected:{' '}
                  {selectedRealtimeSnapshot.metadata?.network?.isConnected === null ||
                  selectedRealtimeSnapshot.metadata?.network?.isConnected === undefined
                    ? 'unknown'
                    : selectedRealtimeSnapshot.metadata?.network?.isConnected
                      ? 'yes'
                      : 'no'}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Lifecycle</Label>
                <SmallText>
                  phase={selectedRealtimeSnapshot.metadata?.lifecycle?.phase ?? ''} / backgroundDurationMs=
                  {selectedRealtimeSnapshot.metadata?.lifecycle?.backgroundDurationMs ?? ''} / uptimeMs=
                  {selectedRealtimeSnapshot.metadata?.lifecycle?.appUptimeMs ?? ''}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Request Health</Label>
                <SmallText>
                  total={selectedRealtimeSnapshot.metadata?.requestHealth?.total ?? 0}, failed=
                  {selectedRealtimeSnapshot.metadata?.requestHealth?.failed ?? 0}, failureRate=
                  {typeof selectedRealtimeSnapshot.metadata?.requestHealth?.failureRate === 'number'
                    ? `${(selectedRealtimeSnapshot.metadata?.requestHealth?.failureRate * 100).toFixed(1)}%`
                    : ''}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Realtime Channels</Label>
                <SmallText>{selectedRealtimeSnapshot.realtime_channels ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Socket State</Label>
                <SmallText>
                  {selectedRealtimeSnapshot.metadata?.realtime?.socketState ?? ''}
                </SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Last Failed Channel</Label>
                {selectedRealtimeSnapshot.metadata?.realtime?.lastFailedChannel ? (
                  <Section>
                    <SmallText>
                      {selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.id} {' '}
                      {selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.topic ?? 'unknown'} {' '}
                      {selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.channelState ?? 'unknown'}
                    </SmallText>
                    <SmallText>
                      {selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.error ?? 'No error message'}
                    </SmallText>
                    {selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.ts ? (
                      <SmallText>
                        {new Date(selectedRealtimeSnapshot.metadata.realtime.lastFailedChannel.ts).toLocaleString()}
                      </SmallText>
                    ) : null}
                  </Section>
                ) : (
                  <SmallText></SmallText>
                )}
              </FieldRow>
              <FieldRow>
                <Label>Channel Details</Label>
                <Section>
                  {(selectedRealtimeSnapshot.metadata?.realtime?.channels ?? []).length === 0 ? (
                    <SmallText></SmallText>
                  ) : (
                    (selectedRealtimeSnapshot.metadata?.realtime?.channels ?? []).map((channel) => (
                      <Section key={channel.id}>
                        <SmallText>
                          {channel.id}  {channel.topic ?? 'unknown'}  {channel.isActive ? 'active' : 'inactive'} {' '}
                          {channel.channelState ?? 'unknown'}
                          {channel.lastReceivedAgeMs !== null && channel.lastReceivedAgeMs !== undefined
                            ? `  last event ${Math.round(channel.lastReceivedAgeMs / 1000)}s ago`
                            : ''}
                        </SmallText>
                        {channel.lastError ? (
                          <SmallText>
                            Last error: {channel.lastError.message}
                            {channel.lastError.ts
                              ? `  ${new Date(channel.lastError.ts).toLocaleString()}`
                              : ''}
                          </SmallText>
                        ) : null}
                      </Section>
                    ))
                  )}
                </Section>
              </FieldRow>
              <FieldRow>
                <Label>Active Ops</Label>
                <SmallText>{selectedRealtimeSnapshot.active_operations ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Pending Ops</Label>
                <SmallText>{selectedRealtimeSnapshot.pending_operations ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Upload Queue Depth</Label>
                <SmallText>{selectedRealtimeSnapshot.upload_queue_depth ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Background State</Label>
                <SmallText>{selectedRealtimeSnapshot.is_in_background ? 'Background' : 'Foreground'}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Snapshot Source</Label>
                <SmallText>{selectedRealtimeSnapshot.source ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Correlated Events</Label>
                <Section>
                  {correlatedEventsLoading ? (
                    <SmallText>Loading correlated events...</SmallText>
                  ) : (
                    <>
                      <SmallText>Telemetry events: {correlatedTelemetryEvents.length}</SmallText>
                      {correlatedTelemetryEvents.slice(0, 6).map((event) => (
                        <SmallText key={event.id}>
                          {new Date(event.created_at).toLocaleString()} / {event.level} / {event.event_name}
                        </SmallText>
                      ))}
                      <SmallText>App errors/warnings: {correlatedAppErrorEvents.length}</SmallText>
                      {correlatedAppErrorEvents.slice(0, 6).map((event) => (
                        <SmallText key={event.id}>
                          {new Date(event.created_at).toLocaleString()} / {event.level} / {truncateText(event.message, 96)}
                        </SmallText>
                      ))}
                    </>
                  )}
                </Section>
              </FieldRow>
            </Section>
          </ModalCard>
        </ModalOverlay>
      )}

      {selectedTelemetryEvent && (
        <ModalOverlay onClick={() => setSelectedTelemetryEvent(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <HeaderRow>
              <div>
                <CardTitle>Telemetry Event Details</CardTitle>
                <CardSubtitle>Diagnostic telemetry captured for monitoring coverage.</CardSubtitle>
              </div>
              <SaveButton type="button" onClick={() => setSelectedTelemetryEvent(null)}>
                Close
              </SaveButton>
            </HeaderRow>
            <Divider />
            <Section>
              <FieldRow>
                <Label>Recorded At</Label>
                <SmallText>{new Date(selectedTelemetryEvent.created_at).toLocaleString()}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Level</Label>
                <LevelBadge $level={selectedTelemetryEvent.level as AppErrorLevel}>{selectedTelemetryEvent.level}</LevelBadge>
              </FieldRow>
              <FieldRow>
                <Label>Event</Label>
                <SmallText>{selectedTelemetryEvent.event_name}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>User</Label>
                <SmallText>{getTelemetryUserLabel(selectedTelemetryEvent.user_id)}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Session ID</Label>
                <SmallText>{selectedTelemetryEvent.session_id ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Source</Label>
                <SmallText>{selectedTelemetryEvent.source ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Message</Label>
                <SmallText>{selectedTelemetryEvent.message ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Payload</Label>
                <Section>
                  <CodeBlock>{formatJsonBlock(selectedTelemetryEvent.payload)}</CodeBlock>
                </Section>
              </FieldRow>
              <FieldRow>
                <Label>Context</Label>
                <Section>
                  <CodeBlock>{formatJsonBlock(selectedTelemetryEvent.context)}</CodeBlock>
                </Section>
              </FieldRow>
              <FieldRow>
                <Label>Tags</Label>
                <Section>
                  <CodeBlock>{formatJsonBlock(selectedTelemetryEvent.tags)}</CodeBlock>
                </Section>
              </FieldRow>
            </Section>
          </ModalCard>
        </ModalOverlay>
      )}

      {selectedAppErrorEvent && (
        <ModalOverlay onClick={() => setSelectedAppErrorEvent(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <HeaderRow>
              <div>
                <CardTitle>App Error Details</CardTitle>
                <CardSubtitle>Captured from the web app error monitor.</CardSubtitle>
              </div>
              <SaveButton type="button" onClick={() => setSelectedAppErrorEvent(null)}>
                Close
              </SaveButton>
            </HeaderRow>
            <Divider />
            <Section>
              <FieldRow>
                <Label>Recorded At</Label>
                <SmallText>{new Date(selectedAppErrorEvent.created_at).toLocaleString()}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Level</Label>
                <LevelBadge $level={selectedAppErrorEvent.level}>{selectedAppErrorEvent.level}</LevelBadge>
              </FieldRow>
              <FieldRow>
                <Label>User</Label>
                <SmallText>{getAppErrorUserLabel(selectedAppErrorEvent.user_id)}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Source</Label>
                <SmallText>{selectedAppErrorEvent.source ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Platform</Label>
                <SmallText>{selectedAppErrorEvent.platform ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>App Version</Label>
                <SmallText>{selectedAppErrorEvent.app_version ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>URL</Label>
                {selectedAppErrorEvent.url ? (
                  <ExternalLink href={selectedAppErrorEvent.url} target="_blank" rel="noreferrer">
                    {selectedAppErrorEvent.url}
                  </ExternalLink>
                ) : (
                  <SmallText></SmallText>
                )}
              </FieldRow>
              <FieldRow>
                <Label>User Agent</Label>
                <SmallText>{selectedAppErrorEvent.user_agent ?? ''}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Message</Label>
                <SmallText>{selectedAppErrorEvent.message}</SmallText>
              </FieldRow>
              <FieldRow>
                <Label>Stack</Label>
                <Section>
                  <CodeBlock>{selectedAppErrorEvent.stack ?? ''}</CodeBlock>
                </Section>
              </FieldRow>
              <FieldRow>
                <Label>Context</Label>
                <Section>
                  <CodeBlock>{formatJsonBlock(selectedAppErrorEvent.context)}</CodeBlock>
                </Section>
              </FieldRow>
              <FieldRow>
                <Label>Tags</Label>
                <Section>
                  <CodeBlock>{formatJsonBlock(selectedAppErrorEvent.tags)}</CodeBlock>
                </Section>
              </FieldRow>
            </Section>
          </ModalCard>
        </ModalOverlay>
      )}

      {activeTab === 'assets' && (
        <Card>
          <CardTitle>Assets</CardTitle>
          <CardSubtitle>Manage uploaded images and files.</CardSubtitle>
          <Section>
            <InlineRow>
              <UploadButton onClick={() => uploadInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Asset'}
              </UploadButton>
              <input
                ref={uploadInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(event) => handleUploadAsset(event.target.files?.[0])}
              />
            </InlineRow>

            {assets.length === 0 && <EmptyState>No assets uploaded yet.</EmptyState>}
            {assets.map((asset) => (
              <AssetRow key={asset.id}>
                <div>
                  <strong>{asset.name}</strong>
                  <SmallText>
                    {asset.type} {' '}
                    {asset.file_size ? `${(asset.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                  </SmallText>
                  <SmallText>{new Date(asset.created_at).toLocaleDateString()}</SmallText>
                  <SmallText>
                    <ExternalLink href={appConfigService.getAssetUrl(asset.file_path)} target="_blank" rel="noreferrer">
                      Open asset
                    </ExternalLink>
                  </SmallText>
                </div>
                <DangerButton onClick={() => handleDeleteAsset(asset.id)}>Delete</DangerButton>
              </AssetRow>
            ))}
          </Section>
        </Card>
      )}
    </Page>
  );
};

export default SuperAdminPage;
